import React, { useEffect, useRef, useState } from "react";
import { BackButton, Button, Card, Chip, Input, Textarea, Select } from "./ui";
import api from "./api";
import { ALLY_CATS } from "./Allies";
import { ACTIVITY_CATS } from "./Activities";
import ActivityChat from "./ActivityChat";
import CityInput from "./CityInput";
import {
  COUNTRY_FILTER_OPTIONS,
  COUNTRY_OPTIONS,
  countryCityPlaceholder,
  countryLabel,
  effectiveTargetCountries,
  matchesCountryFilter,
  matchesTargetFilter,
  targetCountriesLabel,
} from "./countries";

function MarketTargets({ countries, onChange, testId }) {
  return <fieldset data-testid={testId} className="flex flex-wrap gap-3">
    <legend className="sr-only">Mercados de publicación</legend>
    {COUNTRY_OPTIONS.map(([code, label]) => <label key={code} className="flex items-center gap-2">
      <input type="checkbox" value={code} checked={countries.includes(code)}
        onChange={() => onChange(countries.includes(code)
          ? countries.filter(value => value !== code) : [...countries, code])} />{label}
    </label>)}
    {!countries.length && <p role="alert">Selecciona al menos un país.</p>}
  </fieldset>;
}

const TABS = [
  { key: "overview", label: "Resumen" },
  { key: "content", label: "Contenido" },
  { key: "users", label: "Usuarios" },
  { key: "organizations", label: "Organizaciones" },
  { key: "dogs", label: "Perros" },
  { key: "allies", label: "Aliados" },
  { key: "activities", label: "Actividades" },
  { key: "sos", label: "SOS" },
  { key: "reviews", label: "Reseñas" },
  { key: "moderation", label: "Moderación" },
];

function CountryFilterSelect({ value, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 text-xs font-bold text-white/60">
      <span>País</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="rounded-full border border-white/20 bg-[#163954] px-3 py-2 text-xs font-bold text-white outline-none focus:border-[#67D5B5]"
      >
        {COUNTRY_FILTER_OPTIONS.map(([code, label]) => (
          <option key={code} value={code}>
            {label}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function Admin({ onBack }) {
  const [tab, setTab] = useState("overview");

  return (
    <div className="min-h-screen bg-[#0B2D4D]" data-testid="admin-screen">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <div className="mb-5 flex items-center justify-between">
          <BackButton onClick={onBack} testId="admin-back-btn" />
          <span className="rounded-full bg-[#67D5B5] px-3 py-1 text-xs font-black text-[#0B2D4D]">
            PANEL ADMIN · beta
          </span>
        </div>
        <h1 className="font-display text-4xl font-black text-white sm:text-5xl">
          PawCita Admin 🐾
        </h1>

        <div className="mt-5 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`rounded-full px-4 py-2 text-sm font-bold transition ${
                tab === t.key ? "bg-white text-[#0B2D4D]" : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "overview" && <Overview />}
          {tab === "content" && <ContentAdmin />}
          {tab === "users" && <UsersTable />}
          {tab === "organizations" && <OrganizationsAdmin />}
          {tab === "dogs" && <DogsTable />}
          {tab === "allies" && <AlliesAdmin />}
          {tab === "activities" && <ActivitiesAdmin />}
          {tab === "sos" && <SosAdmin />}
          {tab === "reviews" && <ReviewsModeration />}
          {tab === "moderation" && <Moderation />}
        </div>
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.admin.stats().then(setStats).catch((e) => setErr(e.message));
  }, []);
  if (err) return <p className="text-rose-300" data-testid="admin-error">{err}</p>;
  if (!stats) return <p className="text-white/70">Cargando métricas…</p>;
  const cards = [
    ["Usuarios", stats.users_total, "usuarios"],
    ["Perros", stats.dogs_total, "perros"],
    ["Perros con foto", stats.dogs_with_photo, "fotos"],
    ["Solicitudes enviadas", stats.requests_sent, "sent"],
    ["Solicitudes aceptadas", stats.requests_accepted, "accepted"],
    ["Chats activos", stats.chats_active, "chats"],
    ["Bloqueos de contacto", stats.moderation_blocked, "mod"],
  ];
  return (
    <div data-testid="admin-overview">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map(([label, value, id]) => (
          <div key={id} className="rounded-2xl bg-white/10 p-4" data-testid={`stat-${id}`}>
            <p className="font-display text-3xl font-black text-white">{value}</p>
            <p className="mt-1 text-xs font-bold uppercase tracking-wider text-emerald-200">{label}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <MiniSeries title="Usuarios por fecha" data={stats.users_by_date} />
        <MiniSeries title="Perros por fecha" data={stats.dogs_by_date} />
      </div>
    </div>
  );
}

function MiniSeries({ title, data }) {
  return (
    <div className="rounded-2xl bg-white/5 p-4">
      <p className="mb-2 text-xs font-black uppercase tracking-wider text-emerald-200">{title}</p>
      {(!data || data.length === 0) && <p className="text-sm text-white/50">Sin datos aún.</p>}
      <div className="space-y-1">
        {(data || []).slice(-8).map((d) => (
          <div key={d.date} className="flex items-center justify-between text-sm text-white/80">
            <span>{d.date}</span>
            <span className="font-bold text-white">{d.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function useTable(fetcher) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");


  const load = () => {
    setLoading(true);
    fetcher(search, page)
      .then((d) => { setData(d); setErr(""); })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };

  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    setLoading(true);
    fetcher(searchRef.current, page)
      .then((d) => { setData(d); setErr(""); })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [fetcher, page]);
  return { search, setSearch, page, setPage, data, loading, err, load };
}

function Pager({ page, pages, setPage }) {
  return (
    <div className="mt-3 flex items-center justify-between text-sm text-white/80">
      <button disabled={page <= 1} onClick={() => setPage(page - 1)}
        data-testid="admin-prev-page"
        className="rounded-full bg-white/10 px-3 py-1 font-bold disabled:opacity-40">← Anterior</button>
      <span>Página {page} de {pages}</span>
      <button disabled={page >= pages} onClick={() => setPage(page + 1)}
        data-testid="admin-next-page"
        className="rounded-full bg-white/10 px-3 py-1 font-bold disabled:opacity-40">Siguiente →</button>
    </div>
  );
}

function UsersTable() {
  const t = useTable(api.admin.users);
  const toggle = async (u) => {
    await api.admin.setUserStatus(u.id, !u.active);
    t.load();
  };
  return (
    <div data-testid="admin-users">
      <SearchBar t={t} placeholder="Buscar por nombre, email o ciudad" testId="admin-users-search" />
      {t.err && <p className="text-rose-300">{t.err}</p>}
      <div className="mt-3 overflow-x-auto rounded-2xl bg-white/5">
        <table className="w-full text-left text-sm text-white/90">
          <thead className="text-xs uppercase text-emerald-200">
            <tr><th className="p-3">Nombre</th><th className="p-3">Email</th><th className="p-3">Ciudad</th>
            <th className="p-3">Verif.</th><th className="p-3">Perros</th><th className="p-3">Estado</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {t.data.items.map((u) => (
              <tr key={u.id} className="border-t border-white/10" data-testid={`admin-user-${u.id}`}>
                <td className="p-3 font-bold">{u.name} {u.role === "admin" && <span className="text-emerald-300">★</span>}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3">{u.city || "—"}</td>
                <td className="p-3">{u.email_verified ? "✅" : "⏳"}</td>
                <td className="p-3">{u.dog_count}</td>
                <td className="p-3">{u.active ? <span className="text-emerald-300">Activo</span> : <span className="text-rose-300">Inactivo</span>}</td>
                <td className="p-3">
                  <button onClick={() => toggle(u)} data-testid={`admin-user-toggle-${u.id}`}
                    className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold hover:bg-white/20">
                    {u.active ? "Desactivar" : "Activar"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={t.page} pages={t.data.pages} setPage={t.setPage} />
    </div>
  );
}

function DogsTable() {
  const t = useTable(api.admin.dogs);
  const toggle = async (d) => {
    await api.admin.setDogStatus(d.id, !d.active);
    t.load();
  };
  return (
    <div data-testid="admin-dogs">
      <SearchBar t={t} placeholder="Buscar por nombre, raza o dueño" testId="admin-dogs-search" />
      {t.err && <p className="text-rose-300">{t.err}</p>}
      <div className="mt-3 overflow-x-auto rounded-2xl bg-white/5">
        <table className="w-full text-left text-sm text-white/90">
          <thead className="text-xs uppercase text-emerald-200">
            <tr><th className="p-3">Foto</th><th className="p-3">Nombre</th><th className="p-3">Raza</th>
            <th className="p-3">Edad</th><th className="p-3">Sexo</th><th className="p-3">Dueño</th>
            <th className="p-3">Estado</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {t.data.items.map((d) => (
              <tr key={d.id} className="border-t border-white/10" data-testid={`admin-dog-${d.id}`}>
                <td className="p-3">
                  <img src={d.image || "/dog-placeholder.svg"}
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
                    alt={d.name} className="h-10 w-10 rounded-lg object-cover" />
                </td>
                <td className="p-3 font-bold">{d.name}</td>
                <td className="p-3">{d.breed}</td>
                <td className="p-3">{d.age}</td>
                <td className="p-3">{d.sex}</td>
                <td className="p-3">{d.owner || "—"}</td>
                <td className="p-3">{d.active ? <span className="text-emerald-300">Activo</span> : <span className="text-rose-300">Inactivo</span>}</td>
                <td className="p-3">
                  <button onClick={() => toggle(d)} data-testid={`admin-dog-toggle-${d.id}`}
                    className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold hover:bg-white/20">
                    {d.active ? "Desactivar" : "Activar"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={t.page} pages={t.data.pages} setPage={t.setPage} />
    </div>
  );
}

function SearchBar({ t, placeholder, testId }) {
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); t.setPage(1); t.load(); }}
      className="flex gap-2"
    >
      <input
        value={t.search}
        onChange={(e) => t.setSearch(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="flex-1 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm text-white placeholder-white/40 outline-none focus:border-[#67D5B5]"
      />
      <button type="submit" className="rounded-full bg-[#67D5B5] px-4 py-2 text-sm font-bold text-[#0B2D4D]">
        Buscar
      </button>
    </form>
  );
}

function ReviewsModeration() {
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("all");
  const [reportedOnly, setReportedOnly] = useState(false);
  const [savingId, setSavingId] = useState("");

  const load = () => {
    setErr("");
    setList(null);

    api.admin
      .allyReviews({
        status: status === "all" ? "" : status,
        reportedOnly,
      })
      .then(setList)
      .catch((e) => setErr(e.message));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, reportedOnly]);

  const setReviewStatus = async (review, nextStatus) => {
    setSavingId(review.id);
    setErr("");

    try {
      await api.admin.setAllyReviewStatus(review.id, nextStatus);
      await load();
    } catch (e) {
      setErr(e?.message || "No pudimos actualizar la reseña.");
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="text-white" data-testid="admin-reviews">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold">Reseñas de aliados</h3>
          <p className="mt-1 max-w-2xl text-sm text-white/60">
            Modera reseñas reportadas o inapropiadas. Ocultar una reseña la
            retira del promedio público hasta que PawCita decida republicarla.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs font-bold text-white/60">
            <span className="mr-2">Estado</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="admin-reviews-status"
              className="rounded-full border border-white/20 bg-[#163954] px-3 py-2 text-xs font-bold text-white outline-none focus:border-[#67D5B5]"
            >
              <option value="all">Todas</option>
              <option value="published">Publicadas</option>
              <option value="hidden">Ocultas</option>
            </select>
          </label>

          <label className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold text-white/80">
            <input
              type="checkbox"
              checked={reportedOnly}
              onChange={(e) => setReportedOnly(e.target.checked)}
              data-testid="admin-reviews-reported"
            />
            Solo reportadas
          </label>
        </div>
      </div>

      {err ? <p className="mt-5 text-sm text-rose-300">{err}</p> : null}

      {!list && !err ? (
        <p className="mt-6 text-sm text-white/70">Cargando…</p>
      ) : null}

      {Array.isArray(list) && list.length === 0 ? (
        <p className="mt-6 text-sm text-white/60" data-testid="admin-reviews-empty">
          No hay reseñas para estos filtros.
        </p>
      ) : null}

      {Array.isArray(list) && list.length ? (
        <div className="mt-6 space-y-4">
          {list.map((review) => (
            <div
              key={review.id}
              className="rounded-3xl border border-white/10 bg-white/10 p-5"
              data-testid={`admin-review-${review.id}`}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-emerald-200">
                    {review.ally_name || "Aliado PawCita"}
                  </p>
                  <p className="mt-1 font-display text-xl font-black text-white">
                    {review.author_name || "Usuario"}
                  </p>
                  {review.verified_visit ? (
                    <p className="mt-1 text-xs font-black text-emerald-300">
                      ✓ Visita verificada por PawCita
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-amber-50 px-3 py-1 text-sm font-black text-[#0B2D4D]">
                    ★ {review.rating}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-black ${
                      review.status === "hidden"
                        ? "bg-rose-100 text-rose-700"
                        : "bg-emerald-100 text-emerald-700"
                    }`}
                  >
                    {review.status === "hidden" ? "Oculta" : "Publicada"}
                  </span>
                  {review.report_count > 0 ? (
                    <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-black text-rose-700">
                      {review.report_count} {review.report_count === 1 ? "reporte" : "reportes"}
                    </span>
                  ) : null}
                </div>
              </div>

              {review.comment ? (
                <p className="mt-4 text-sm leading-6 text-white/80">
                  {review.comment}
                </p>
              ) : (
                <p className="mt-4 text-sm italic text-white/45">Sin comentario.</p>
              )}

              {review.merchant_reply?.text ? (
                <div className="mt-4 rounded-2xl bg-white/5 p-4">
                  <p className="text-xs font-black uppercase tracking-wide text-white/50">
                    Respuesta del establecimiento
                  </p>
                  <p className="mt-2 text-sm leading-6 text-white/75">
                    {review.merchant_reply.text}
                  </p>
                </div>
              ) : null}

              <div className="mt-5 flex flex-wrap justify-end gap-2">
                {review.status === "hidden" ? (
                  <Button
                    variant="outline"
                    disabled={savingId === review.id}
                    onClick={() => setReviewStatus(review, "published")}
                    testId={`admin-review-publish-${review.id}`}
                  >
                    {savingId === review.id ? "Guardando…" : "Republicar"}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    disabled={savingId === review.id}
                    onClick={() => setReviewStatus(review, "hidden")}
                    testId={`admin-review-hide-${review.id}`}
                  >
                    {savingId === review.id ? "Guardando…" : "Ocultar"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}


function Moderation() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.admin.moderation().then(setData).catch((e) => setErr(e.message));
  }, []);
  if (err) return <p className="text-rose-300">{err}</p>;
  if (!data) return <p className="text-white/70">Cargando…</p>;
  const labels = { telefono: "Teléfono", email: "Email", link: "Enlaces", red_social: "Redes sociales", otro: "Otro" };
  return (
    <div data-testid="admin-moderation">
      <div className="rounded-2xl bg-white/10 p-4">
        <p className="font-display text-3xl font-black text-white">{data.total}</p>
        <p className="text-xs font-bold uppercase tracking-wider text-emerald-200">
          Intentos de compartir contacto bloqueados
        </p>
      </div>
      <p className="mt-4 text-xs text-white/50">
        Por privacidad solo se registra el tipo de intento, el usuario y la fecha — nunca el texto del mensaje.
      </p>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {Object.entries(data.by_type).map(([k, v]) => (
          <div key={k} className="rounded-2xl bg-white/5 p-3">
            <p className="font-display text-2xl font-black text-white">{v}</p>
            <p className="text-xs text-emerald-200">{labels[k] || k}</p>
          </div>
        ))}
        {Object.keys(data.by_type).length === 0 && (
          <p className="text-sm text-white/50">Sin eventos registrados aún.</p>
        )}
      </div>
    </div>
  );
}


const EMPTY_ORGANIZATION = {
  organization_name: "",
  description: "",
  email: "",
  country_code: "PA",
  city: "",
  province: "",
  responsible_name: "",
  phone: "",
  logo: "",
  active: true,
  access_setup_required: true,
};

function OrganizationsAdmin() {
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("list");
  const [form, setForm] = useState(EMPTY_ORGANIZATION);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef("");
  const [countryFilter, setCountryFilter] = useState("ALL");
  const [deletingId, setDeletingId] = useState(null);

  const load = (term = searchRef.current) => {
    setErr("");
    api.admin
      .organizations(term, countryFilter === "ALL" ? undefined : countryFilter)
      .then(setList)
      .catch((e) => setErr(e.message));
  };

  useEffect(load, [countryFilter]);

  const set = (key) => (e) => {
    const value =
      e && e.target
        ? e.target.type === "checkbox"
          ? e.target.checked
          : e.target.value
        : e;

    setForm((current) => ({ ...current, [key]: value }));
  };

  const openCreate = () => {
    setForm(EMPTY_ORGANIZATION);
    setEditId(null);
    setErr("");
    setMode("form");
  };

  const openEdit = (organization) => {
    setForm({
      ...EMPTY_ORGANIZATION,
      ...organization,
    });
    setEditId(organization.id);
    setErr("");
    setMode("form");
  };

  const submit = async () => {
    setErr("");

    if (!form.organization_name.trim()) {
      setErr("El nombre de la organización es obligatorio.");
      return;
    }

    if (!form.email.trim()) {
      setErr("El correo de acceso es obligatorio.");
      return;
    }

    const payload = {
      organization_name: form.organization_name.trim(),
      description: form.description.trim(),
      email: form.email.trim(),
      country_code: form.country_code || "PA",
      city: form.city.trim(),
      province: form.province.trim(),
      responsible_name: form.responsible_name.trim(),
      phone: form.phone.trim(),
    };

    setSaving(true);

    try {
      if (editId) {
        const updated = await api.admin.updateOrganization(editId, payload);
        setForm((current) => ({
          ...current,
          ...updated,
        }));
      } else {
        const created = await api.admin.createOrganization({
          ...payload,
          active: true,
        });

        setEditId(created.id);
        setForm((current) => ({
          ...current,
          ...created,
        }));
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files && e.target.files[0];

    if (!file || !editId) return;

    setSaving(true);
    setErr("");

    try {
      const result = await api.admin.uploadOrganizationLogo(editId, file);

      setForm((current) => ({
        ...current,
        logo: result.logo,
      }));
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (organization) => {
    setErr("");

    try {
      await api.admin.setOrganizationStatus(
        organization.id,
        !organization.active
      );
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  const deleteOrganization = async (organization) => {
    setErr("");

    const expectedName = organization.organization_name || "";
    const typedName = window.prompt(
      `Esta acción eliminará definitivamente la organización, sus perros, adopciones y datos asociados.\n\nEscribe exactamente "${expectedName}" para confirmar:`
    );

    if (typedName === null) return;

    if (typedName.trim() !== expectedName.trim()) {
      setErr("El nombre ingresado no coincide. La organización no fue eliminada.");
      return;
    }

    setDeletingId(organization.id);

    try {
      await api.admin.deleteOrganization(organization.id);
      load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const searchOrganizations = (e) => {
    e.preventDefault();
    load(search);
  };

  const visibleOrganizations = (list || []).filter((organization) =>
    matchesCountryFilter(organization, countryFilter)
  );

  if (mode === "form") {
    return (
      <div className="text-white" data-testid="admin-organization-form">
        <BackButton
          onClick={() => {
            setMode("list");
            load();
          }}
          label="Volver a organizaciones"
          testId="organization-form-back"
        />

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-lg font-semibold">
            {editId ? "Editar organización" : "Nueva organización"}
          </h3>

          {editId ? (
            <span
              className={`rounded-full px-3 py-1 text-xs font-black ${
                form.access_setup_required
                  ? "bg-amber-200 text-amber-900"
                  : "bg-emerald-200 text-emerald-900"
              }`}
              data-testid="organization-access-status"
            >
              {form.access_setup_required
                ? "Acceso pendiente"
                : "Acceso configurado"}
            </span>
          ) : null}
        </div>

        {err ? (
          <p
            className="mt-2 text-sm text-red-300"
            data-testid="organization-form-error"
          >
            {err}
          </p>
        ) : null}

        <div className="mt-4 rounded-3xl bg-white p-4 text-[#0B2D4D] sm:p-5">
          <div className="mb-5">
            <p className="mb-1.5 text-sm font-semibold">Logo de la organización</p>

            {editId ? (
              <div className="flex items-center gap-3">
                <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-slate-100">
                  {form.logo ? (
                    <img
                      src={form.logo}
                      alt={form.organization_name || "Organización"}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl">🐾</span>
                  )}
                </div>

                <label className="cursor-pointer rounded-full bg-[#0B2D4D] px-4 py-2 text-sm font-bold text-white">
                  {saving ? "Procesando…" : "Subir logo"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={uploadLogo}
                    data-testid="organization-logo-input"
                  />
                </label>
              </div>
            ) : (
              <p className="text-xs text-slate-500">
                Guarda primero la organización para poder subir su logo.
              </p>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Nombre de la organización *"
              value={form.organization_name}
              onChange={set("organization_name")}
              testId="organization-name"
            />

            <Input
              label="Correo de acceso *"
              value={form.email}
              onChange={set("email")}
              testId="organization-email"
              placeholder="organizacion@correo.com"
            />

            <Select
              label="País *"
              value={form.country_code || "PA"}
              onChange={(e) =>
                setForm((current) => ({
                  ...current,
                  country_code: e.target.value,
                  city: "",
                }))
              }
              testId="organization-country"
            >
              {COUNTRY_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>

            <CityInput
              countryCode={form.country_code || "PA"}
              value={form.city}
              onChange={(value) =>
                setForm((current) => ({ ...current, city: value }))
              }
              label="Ciudad"
              testId="organization-city"
            />

            <Input
              label="Provincia / estado"
              value={form.province}
              onChange={set("province")}
              testId="organization-province"
            />

            <Input
              label="Responsable · solo Admin"
              value={form.responsible_name}
              onChange={set("responsible_name")}
              testId="organization-responsible"
            />

            <Input
              label="Teléfono · solo Admin"
              value={form.phone}
              onChange={set("phone")}
              testId="organization-phone"
            />
          </div>

          <div className="mt-3">
            <Textarea
              label="Descripción pública"
              value={form.description}
              onChange={set("description")}
              testId="organization-description"
              rows={4}
            />
          </div>

          {editId ? (
            <div className="mt-4 rounded-2xl bg-amber-50 p-4 text-sm text-amber-900">
              {form.access_setup_required ? (
                <>
                  <p className="font-black">Acceso aún no configurado</p>
                  <p className="mt-1">
                    La organización debe abrir PawCita, elegir “Olvidé mi contraseña”
                    y usar <strong>{form.email}</strong> para crear su propia contraseña.
                    PawCita no muestra ni entrega contraseñas temporales.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-black text-emerald-800">
                    Acceso configurado ✓
                  </p>
                  <p className="mt-1 text-emerald-800">
                    La organización ya estableció su contraseña de acceso.
                  </p>
                </>
              )}
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap gap-2">
            <Button
              onClick={submit}
              disabled={saving}
              data-testid="organization-save-btn"
            >
              {saving
                ? "Guardando…"
                : editId
                  ? "Guardar cambios"
                  : "Crear organización"}
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setMode("list");
                load();
              }}
              disabled={saving}
            >
              Volver
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-white" data-testid="admin-organizations">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">
            Organizaciones de adopción
          </h3>
          <p className="mt-1 text-xs text-white/60">
            Cuentas verificadas creadas y administradas directamente por PawCita.
          </p>
        </div>

        <Button onClick={openCreate} data-testid="organization-create-btn">
          + Nueva organización
        </Button>
      </div>

      <form
        onSubmit={searchOrganizations}
        className="mt-4 flex gap-2"
      >
        <input
          value={search}
          onChange={(e) => {
            searchRef.current = e.target.value;
            setSearch(e.target.value);
          }}
          placeholder="Buscar por organización, correo, ciudad o responsable"
          data-testid="organizations-search"
          className="flex-1 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm text-white placeholder-white/40 outline-none focus:border-[#67D5B5]"
        />

        <button
          type="submit"
          className="rounded-full bg-[#67D5B5] px-4 py-2 text-sm font-bold text-[#0B2D4D]"
        >
          Buscar
        </button>
      </form>

      <div className="mt-3 flex justify-end">
        <CountryFilterSelect
          value={countryFilter}
          onChange={setCountryFilter}
          testId="organizations-country-filter"
        />
      </div>

      {err ? (
        <p className="mt-3 text-sm text-red-300">{err}</p>
      ) : null}

      {list === null ? (
        <p className="mt-6 text-sm text-white/50">
          Cargando organizaciones…
        </p>
      ) : visibleOrganizations.length === 0 ? (
        <div
          className="mt-6 rounded-2xl bg-white/5 p-6 text-center"
          data-testid="admin-organizations-empty"
        >
          <p className="text-3xl">🐾</p>
          <p className="mt-2 font-semibold">
            Aún no has creado organizaciones de adopción.
          </p>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {visibleOrganizations.map((organization) => (
            <div
              key={organization.id}
              className="rounded-2xl bg-white/10 p-4"
              data-testid={`admin-organization-${organization.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 gap-3">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white/10">
                    {organization.logo ? (
                      <img
                        src={organization.logo}
                        alt={organization.organization_name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl">🐾</span>
                    )}
                  </div>

                  <div className="min-w-0">
                    <p className="font-bold">
                      {organization.organization_name}
                      {!organization.active ? (
                        <span className="ml-2 text-xs text-red-300">
                          (inactiva)
                        </span>
                      ) : null}
                    </p>

                    <p className="mt-1 break-all text-xs text-white/60">
                      {organization.email}
                    </p>

                    <span className="mt-2 inline-block rounded-full bg-white/10 px-2.5 py-1 text-xs font-bold text-emerald-100">
                      {countryLabel(organization.country_code)}
                    </span>

                    <p className="mt-1 text-xs text-white/60">
                      {[organization.city, organization.province]
                        .filter(Boolean)
                        .join(" · ") || "Sin ubicación"}
                    </p>

                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      <span className="rounded-full bg-white/10 px-2.5 py-1">
                        🐕 {organization.dog_count || 0} perros
                      </span>

                      <span className="rounded-full bg-white/10 px-2.5 py-1">
                        🏡 {organization.adoption_count || 0} adopciones
                      </span>

                      <span
                        className={`rounded-full px-2.5 py-1 font-bold ${
                          organization.access_setup_required
                            ? "bg-amber-200 text-amber-900"
                            : "bg-emerald-200 text-emerald-900"
                        }`}
                      >
                        {organization.access_setup_required
                          ? "Acceso pendiente"
                          : "Acceso configurado"}
                      </span>
                    </div>

                    {organization.responsible_name ? (
                      <p className="mt-2 text-xs text-white/50">
                        Responsable: {organization.responsible_name}
                      </p>
                    ) : null}

                    {organization.phone ? (
                      <p className="text-xs text-white/50">
                        Teléfono interno: {organization.phone}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => openEdit(organization)}
                    data-testid={`organization-edit-${organization.id}`}
                  >
                    Editar
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => toggleStatus(organization)}
                    data-testid={`organization-toggle-${organization.id}`}
                  >
                    {organization.active ? "Desactivar" : "Activar"}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => deleteOrganization(organization)}
                    disabled={deletingId === organization.id}
                    className="border-red-300 text-red-200 hover:bg-red-500/10"
                    data-testid={`organization-delete-${organization.id}`}
                  >
                    {deletingId === organization.id ? "Eliminando…" : "Eliminar"}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const EMPTY_ALLY = {
  name: "",
  category: "veterinaria",
  country_code: "PA",
  city: "",
  zone: "",
  short_description: "",
  long_description: "",
  address: "",
  whatsapp: "",
  instagram: "",
  website: "",
  hours: "",
  services: "",
  benefit: "",
  is_founder: false,
  is_verified: false,
  active: true,
  // --- Veterinary emergency MVP ---
  phone: "",
  emergency_service: false,
  open_24_hours: false,
  opening_hours: null,
  lat: "",
  lng: "",
  last_verified_at: "",
  is_pawcita_partner: false,
  access_email: "",
  access_responsible_name: "",
  access_status: "none",
};

const VET_DAYS = [
  ["monday", "Lunes"], ["tuesday", "Martes"], ["wednesday", "Miércoles"],
  ["thursday", "Jueves"], ["friday", "Viernes"], ["saturday", "Sábado"], ["sunday", "Domingo"],
];

function AlliesAdmin() {
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("list"); // list | form | metrics
  const [form, setForm] = useState(EMPTY_ALLY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [baseOpen, setBaseOpen] = useState("08:00");
  const [baseClose, setBaseClose] = useState("18:00");
  const [geoCands, setGeoCands] = useState(null); // null | [{label,lat,lng}]
  const [geocoding, setGeocoding] = useState(false);
  const [invitationPath, setInvitationPath] = useState("");
  const [countryFilter, setCountryFilter] = useState("ALL");

  const invitationUrl = invitationPath
    ? `${window.location.origin}${invitationPath}`
    : "";

  const copyInvitation = async () => {
    if (!invitationUrl) return;

    try {
      await navigator.clipboard.writeText(invitationUrl);
      window.alert("Invitación copiada.");
    } catch {
      window.prompt(
        "Copia este enlace de invitación:",
        invitationUrl
      );
    }
  };

  const reissueInvitation = async () => {
    if (!editId) return;

    if (!(form.access_email || "").trim()) {
      setErr("Indica el correo de acceso.");
      return;
    }

    if (!form.access_status || form.access_status === "none") {
      await submit();
      return;
    }

    setSaving(true);
    setErr("");

    try {
      const updated = await api.admin.inviteAlly(
        editId,
        {
          email: form.access_email.trim(),
          responsible_name:
            (form.access_responsible_name || "").trim(),
        }
      );

      setForm((current) => ({
        ...current,
        ...updated,
        services: Array.isArray(updated.services)
          ? updated.services.join(", ")
          : current.services,
      }));

      setInvitationPath(
        updated.invitation_path || ""
      );
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const applyHoursToAllWeek = () => {
    if (!baseOpen || !baseClose) {
      setErr("Indica una hora de apertura y cierre base antes de aplicar.");
      return;
    }
    const hasExisting =
      form.opening_hours &&
      VET_DAYS.some(([k]) => {
        const d = form.opening_hours[k];
        return d && (d.open || d.close || d.closed);
      });
    if (hasExisting && !window.confirm("Ya hay horarios configurados. ¿Reemplazarlos en todos los días con este horario base?")) {
      return;
    }
    const next = {};
    VET_DAYS.forEach(([k]) => {
      next[k] = { open: baseOpen, close: baseClose, closed: false };
    });
    setForm((f) => ({ ...f, opening_hours: next }));
  };

  const load = () => {
    setErr("");
    api.admin
      .allies("", countryFilter === "ALL" ? undefined : countryFilter)
      .then(setList)
      .catch((e) => setErr(e.message));
  };
  useEffect(load, [countryFilter]);

  const openCreate = () => {
    setForm(EMPTY_ALLY);
    setEditId(null);
    setInvitationPath("");
    setMode("form");
  };
  const openEdit = (a) => {
    setInvitationPath("");
    setForm({
      ...EMPTY_ALLY,
      ...a,
      lat: a.lat ?? "",
      lng: a.lng ?? "",
      opening_hours: a.opening_hours || null,
      last_verified_at: a.last_verified_at || "",
      services: Array.isArray(a.services) ? a.services.join(", ") : "",
    });
    setEditId(a.id);
    setMode("form");
  };
  const openMetrics = (a) => {
    setMetrics({ ally: a, data: null });
    setMode("metrics");
    api.admin
      .allyMetrics(a.id)
      .then((d) => setMetrics({ ally: a, data: d }))
      .catch((e) => setErr(e.message));
  };

  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const buildPayload = (latVal, lngVal) => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      ...form,
      // Existing access changes are applied only by reissuing an invitation.
      access_email: form.is_pawcita_partner && (!form.access_status || form.access_status === "none")
        ? form.access_email : undefined,
      access_responsible_name: form.is_pawcita_partner && (!form.access_status || form.access_status === "none")
        ? form.access_responsible_name : undefined,
      services: form.services
        ? form.services.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
      lat: latVal === "" || latVal == null ? null : parseFloat(latVal),
      lng: lngVal === "" || lngVal == null ? null : parseFloat(lngVal),
      opening_hours: form.opening_hours || null,
      last_verified_at: form.is_verified ? (form.last_verified_at || today) : (form.last_verified_at || null),
    };
  };

  const doSave = async (payload) => {
    setSaving(true);

    try {
      if (editId) {
        const updated = await api.admin.updateAlly(
          editId,
          payload
        );

        setGeoCands(null);

        const needsAccessSetup =
          payload.is_pawcita_partner &&
          (!form.access_status ||
            form.access_status === "none");

        if (updated.invitation_path || needsAccessSetup) {
          const invited = updated.invitation_path ? updated : await api.admin.inviteAlly(
            editId,
            {
              email: (
                form.access_email || ""
              ).trim(),
              responsible_name: (
                form.access_responsible_name || ""
              ).trim(),
            }
          );

          setForm((current) => ({
            ...current,
            ...updated,
            ...invited,
            services: Array.isArray(
              invited.services
            )
              ? invited.services.join(", ")
              : current.services,
          }));

          setInvitationPath(
            invited.invitation_path || ""
          );

          load();
          return;
        }

        setMode("list");
        load();
        return;
      }

      const created = await api.admin.createAlly(payload);

      setGeoCands(null);

      if (created?.invitation_path) {
        setEditId(created.id);
        setInvitationPath(created.invitation_path);

        setForm((current) => ({
          ...current,
          ...created,
          services: Array.isArray(created.services)
            ? created.services.join(", ")
            : current.services,
          lat: created.lat ?? current.lat,
          lng: created.lng ?? current.lng,
          access_email:
            created.access_email || current.access_email,
          access_responsible_name:
            created.access_responsible_name ||
            current.access_responsible_name,
        }));

        load();
        return;
      }

      setMode("list");
      load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setErr("");
    setGeoCands(null);
    if (!form.name.trim() || !form.city.trim() || !form.short_description.trim()) {
      setErr("Nombre, ciudad y descripción corta son obligatorios.");
      return;
    }

    if (
      form.is_pawcita_partner &&
      (!form.access_status || form.access_status === "none") &&
      !(form.access_email || "").trim()
    ) {
      setErr(
        "El correo de acceso es obligatorio para un Aliado PawCita."
      );
      return;
    }

    const hasCoords =
      form.lat !== "" && form.lat != null && form.lng !== "" && form.lng != null;

    const original =
      editId && Array.isArray(list)
        ? list.find((a) => a.id === editId)
        : null;

    const locationFieldsChanged =
      !!original &&
      ((form.address || "").trim() !== (original.address || "").trim() ||
        (form.zone || "").trim() !== (original.zone || "").trim() ||
        (form.city || "").trim() !== (original.city || "").trim() ||
        (form.country_code || "PA") !== (original.country_code || "PA"));

    const coordsChanged =
      !!original &&
      (String(form.lat ?? "") !== String(original.lat ?? "") ||
        String(form.lng ?? "") !== String(original.lng ?? ""));

    const hasManualCoords =
      hasCoords && (!editId || !original || coordsChanged);

    // If the address context changed, prefilled coordinates belong to the old address.
    // Clear them if the address was removed, or geocode the new address below.
    if (locationFieldsChanged && !hasManualCoords && !form.address.trim()) {
      await doSave(buildPayload(null, null));
      return;
    }

    // Resolve the human address when creating, when its location context changed,
    // or when an existing ally does not have coordinates yet.
    if (
      !hasManualCoords &&
      form.address.trim() &&
      (!editId || locationFieldsChanged || !hasCoords)
    ) {
      setGeocoding(true);
      try {
        const r = await api.admin.geocodeAlly({
          address: form.address,
          zone: form.zone,
          city: form.city,
          country_code: form.country_code || "PA",
        });
        if (r.status === "ok") {
          const c = r.candidates[0];
          setForm((f) => ({ ...f, lat: String(c.lat), lng: String(c.lng) }));
          await doSave(buildPayload(c.lat, c.lng));
          return;
        }
        if (r.status === "multiple") {
          setGeoCands(r.candidates);
          return;
        }
        // Si la dirección no se puede geocodificar, guarda el aliado sin coordenadas.
        // La ubicación de mapa puede añadirse manualmente después.
        await doSave(buildPayload(null, null));
        return;
      } catch (e) {
        setErr(e.message);
        return;
      } finally {
        setGeocoding(false);
      }
    }
    await doSave(buildPayload(form.lat, form.lng));
  };

  const chooseCandidate = async (c) => {
    setForm((f) => ({ ...f, lat: String(c.lat), lng: String(c.lng) }));
    setGeoCands(null);
    await doSave(buildPayload(c.lat, c.lng));
  };

  const toggleStatus = async (a) => {
    try {
      await api.admin.setAllyStatus(a.id, !a.active);
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  const visibleAllies = (list || []).filter((ally) =>
    matchesCountryFilter(ally, countryFilter)
  );

  const uploadLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !editId) return;
    setSaving(true);
    setErr("");
    try {
      const r = await api.admin.uploadAllyPhoto(editId, file);
      setForm((f) => ({ ...f, image: r.image }));
    } catch (err) {
      setErr(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (mode === "form") {
    return (
      <div data-testid="admin-ally-form" className="text-white">
        <BackButton onClick={() => setMode("list")} label="Volver a aliados" testId="ally-form-back" />
        <h3 className="mt-3 text-lg font-semibold">{editId ? "Editar aliado" : "Nuevo aliado"}</h3>
        {err && (
          <p className="mt-2 text-sm text-red-300" data-testid="ally-form-error">
            {err}
          </p>
        )}
        <div className="mt-4 rounded-3xl bg-white p-4 text-[#0B2D4D] sm:p-5">
          <div className="mb-4">
            <p className="mb-1.5 text-sm font-semibold">Logo / foto</p>
            {editId ? (
              <div className="flex items-center gap-3">
                <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl bg-slate-100">
                  {form.image ? (
                    <img src={form.image} alt="logo" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-2xl">🐾</span>
                  )}
                </div>
                <label className="cursor-pointer rounded-full bg-[#0B2D4D] px-4 py-2 text-sm font-bold text-white">
                  {saving ? "Subiendo…" : "Subir logo"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="ally-photo-input" />
                </label>
              </div>
            ) : (
              <p className="text-xs text-slate-500">Guarda el aliado primero para subir su logo/foto.</p>
            )}
          </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Nombre *" value={form.name} onChange={set("name")} testId="ally-name" />
          <Select label="Categoría *" value={form.category} onChange={set("category")} testId="ally-category">
            {ALLY_CATS.map((c) => (
              <option key={c.key} value={c.key}>
                {c.emoji} {c.label}
              </option>
            ))}
          </Select>
          <Select
            label="País *"
            value={form.country_code || "PA"}
            onChange={(e) =>
              setForm((current) => ({
                ...current,
                country_code: e.target.value,
                city: "",
              }))
            }
            testId="ally-country"
          >
            {COUNTRY_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <CityInput
            countryCode={form.country_code || "PA"}
            value={form.city}
            onChange={(value) =>
              setForm((current) => ({ ...current, city: value }))
            }
            label="Ciudad *"
            testId="ally-city"
          />
          <Input label="Zona" value={form.zone} onChange={set("zone")} testId="ally-zone" />
          <Input label="WhatsApp (con código de país)" value={form.whatsapp} onChange={set("whatsapp")} testId="ally-whatsapp" />
          <Input
            label="Dirección"
            value={form.address}
            onChange={set("address")}
            testId="ally-address"
            placeholder={
              countryCityPlaceholder(form.country_code)
            }
          />
          <p className="-mt-1 text-xs text-[#6B7A90]">Escribe la dirección (PawCita la combina con la zona y la ciudad) y guarda: intentará obtener las coordenadas automáticamente. Si no encuentra una coincidencia, el aliado se guardará sin ubicación de mapa y podrás agregar latitud/longitud manualmente.</p>
          {geoCands && (
            <div className="mt-1 rounded-2xl border border-slate-200 bg-white p-3" data-testid="ally-geocode-candidates">
              <p className="mb-2 text-sm font-semibold text-[#0B2D4D]">
                {geoCands.length ? "PawCita encontró varias ubicaciones. Elige la correcta:" : "Sin ubicaciones."}
              </p>
              <div className="space-y-2">
                {geoCands.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2">
                    <span className="text-xs text-slate-600" data-testid={`ally-geocode-candidate-label-${i}`}>{c.label}</span>
                    <button
                      type="button"
                      onClick={() => chooseCandidate(c)}
                      disabled={saving}
                      className="shrink-0 rounded-full bg-[#0B2D4D] px-3 py-1 text-xs font-bold text-white hover:opacity-90 disabled:opacity-50"
                      data-testid={`ally-geocode-use-${i}`}>
                      Usar esta ubicación
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500">¿Ninguna es correcta? Ajusta la dirección o ingresa latitud/longitud manualmente.</p>
            </div>
          )}
          <Input label="Latitud (opcional)" value={form.lat} onChange={set("lat")} testId="ally-lat" placeholder="Ej. 8.9824" />
          <Input label="Longitud (opcional)" value={form.lng} onChange={set("lng")} testId="ally-lng" placeholder="Ej. -79.5199" />
          <Input label="Instagram (URL)" value={form.instagram} onChange={set("instagram")} testId="ally-instagram" />
          <Input label="Website (URL)" value={form.website} onChange={set("website")} testId="ally-website" />
          <Input label="Horarios" value={form.hours} onChange={set("hours")} testId="ally-hours" />
          <Input label="Beneficio PawCita" value={form.benefit} onChange={set("benefit")} testId="ally-benefit" />
        </div>
        <div className="mt-3 grid gap-3">
          <Input label="Descripción corta *" value={form.short_description} onChange={set("short_description")} testId="ally-short" />
          <Textarea label="Descripción larga" value={form.long_description} onChange={set("long_description")} testId="ally-long" rows={3} />
          <Input
            label="Especialidades y servicios (separados por coma)"
            value={form.services}
            onChange={set("services")}
            testId="ally-services"
            placeholder="Ej. Medicina general, Ortopedia, Laboratorio"
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2" data-testid="ally-founder-toggle">
            <input type="checkbox" checked={form.is_founder} onChange={set("is_founder")} /> Aliado Fundador
          </label>
          <label className="flex items-center gap-2" data-testid="ally-verified-toggle">
            <input type="checkbox" checked={form.is_verified} onChange={set("is_verified")} /> Aliado Verificado
          </label>
          <label className="flex items-center gap-2" data-testid="ally-active-toggle">
            <input type="checkbox" checked={form.active} onChange={set("active")} /> Activo
          </label>
          <label className="flex items-center gap-2" data-testid="ally-partner-toggle">
            <input
              type="checkbox"
              checked={form.is_pawcita_partner}
              onChange={set("is_pawcita_partner")}
            />
            Aliado PawCita
          </label>
        </div>

        {form.is_pawcita_partner && (
          <div
            className="mt-5 rounded-2xl border border-[#67D5B5]/50 bg-[#F1FFFA] p-4"
            data-testid="ally-access-section"
          >
            <p className="text-sm font-black uppercase tracking-[0.14em] text-[#0B2D4D]">
              🔐 Acceso al panel del aliado
            </p>

            <p className="mt-1 text-xs leading-5 text-[#6B7A90]">
              Este correo será utilizado para ingresar al portal
              privado del establecimiento.
            </p>

            <p className="mt-2 text-xs leading-5 text-[#6B7A90]">
              {form.access_status === "active"
                ? "El correo y el responsable no se pueden editar. Para recuperar la contraseña, usa el Portal de Aliados."
                : form.access_status === "pending"
                ? "Guardar cambios no modifica el correo ni el responsable. Para aplicarlos, pulsa Generar nueva invitación: reemplaza e invalida el enlace anterior."
                : "El correo y el responsable se usarán para crear la primera invitación al guardar o pulsar Crear invitación."}
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Input
                label="Correo de acceso *"
                type="email"
                value={form.access_email || ""}
                onChange={set("access_email")}
                readOnly={form.access_status === "active"}
                testId="ally-access-email"
                placeholder="correo@establecimiento.com"
              />

              <Input
                label="Nombre del responsable"
                value={form.access_responsible_name || ""}
                onChange={set("access_responsible_name")}
                readOnly={form.access_status === "active"}
                testId="ally-access-responsible"
                placeholder="Ej. María González"
              />
            </div>

            {editId ? (
              <div className="mt-4 rounded-xl bg-white p-3">
                <p className="text-xs font-black uppercase tracking-wide text-[#6B7A90]">
                  Estado del acceso
                </p>

                <p
                  className="mt-1 font-black text-[#0B2D4D]"
                  data-testid="ally-access-status"
                >
                  {form.access_status === "active"
                    ? "✓ Acceso configurado"
                    : form.access_status === "pending"
                    ? "Invitación pendiente"
                    : "Sin acceso configurado"}
                </p>

                {form.access_status === "pending" ? (
                  <div className="mt-2 space-y-1 text-xs text-[#6B7A90]">
                    {form.access_email ? (
                      <p data-testid="ally-invite-email-to">
                        <span className="font-black uppercase tracking-wide text-[#0B2D4D]">
                          Correo:
                        </span>{" "}
                        {form.access_email}
                      </p>
                    ) : null}
                    {form.access_invitation_email_status ? (
                      <p data-testid="ally-invite-email-status">
                        <span className="font-black uppercase tracking-wide text-[#0B2D4D]">
                          Estado de correo:
                        </span>{" "}
                        {form.access_invitation_email_status === "sent"
                          ? "Enviado"
                          : form.access_invitation_email_status === "failed"
                          ? "No enviado"
                          : "Pendiente"}
                      </p>
                    ) : null}
                    {form.access_invitation_email_status === "failed" ? (
                      <p
                        className="text-[#B91C1C]"
                        data-testid="ally-invite-email-error"
                      >
                        Invitación creada, pero no pudimos enviar el correo.
                        Puedes copiar el enlace y enviarlo manualmente.
                      </p>
                    ) : null}
                    {form.access_invitation_email_sent_at ? (
                      <p data-testid="ally-invite-email-sent-at">
                        <span className="font-black uppercase tracking-wide text-[#0B2D4D]">
                          Último envío:
                        </span>{" "}
                        {new Date(
                          form.access_invitation_email_sent_at,
                        ).toLocaleString()}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {form.access_status !== "active" ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={reissueInvitation}
                      disabled={saving}
                      testId="ally-reissue-invite"
                    >
                      {form.access_status === "pending"
                        ? "Generar nueva invitación"
                        : "Crear invitación"}
                    </Button>

                    {invitationUrl ? (
                      <Button
                        type="button"
                        variant="navy"
                        onClick={copyInvitation}
                        testId="ally-copy-invite"
                      >
                        Copiar invitación
                      </Button>
                    ) : null}
                  </div>
                ) : null}

                {invitationUrl ? (
                  <div
                    className="mt-3 break-all rounded-xl bg-slate-50 p-3 text-xs text-slate-600"
                    data-testid="ally-invitation-url"
                  >
                    {invitationUrl}
                  </div>
                ) : null}

                {form.access_status === "active" ? (
                  <div className="mt-3">
                    <p className="text-xs text-[#6B7A90]">
                      El establecimiento ya puede ingresar con su
                      correo y contraseña. Si olvidó la contraseña,
                      puede recuperarla directamente desde el portal.
                    </p>

                    <a
                      href="/aliados"
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex rounded-full bg-[#0B2D4D] px-4 py-2 text-xs font-black text-white transition hover:opacity-90"
                      data-testid="ally-open-portal"
                    >
                      Abrir Portal de Aliados ↗
                    </a>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="mt-3 text-xs text-[#6B7A90]">
                Al crear el aliado, PawCita generará un enlace
                seguro para que el establecimiento defina su contraseña.
              </p>
            )}
          </div>
        )}

        {form.category === "veterinaria" && (
          <div className="mt-5 rounded-2xl bg-slate-50 p-4" data-testid="ally-vet-section">
            <p className="mb-3 text-sm font-black uppercase tracking-[0.14em] text-[#FF5C5C]">🚑 Emergencia veterinaria</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Teléfono (para llamar)" value={form.phone} onChange={set("phone")} testId="ally-phone" />
              <Input label="Verificada el (YYYY-MM-DD)" value={form.last_verified_at} onChange={set("last_verified_at")} testId="ally-last-verified" placeholder="2026-06-01" />
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2" data-testid="ally-emergency-toggle">
                <input type="checkbox" checked={form.emergency_service} onChange={set("emergency_service")} /> Atiende emergencias
              </label>
              <label className="flex items-center gap-2" data-testid="ally-24h-toggle">
                <input type="checkbox" checked={form.open_24_hours} onChange={set("open_24_hours")} /> Abierto 24 horas
              </label>
            </div>
            {!form.open_24_hours && (
              <div className="mt-4" data-testid="ally-opening-hours">
                <p className="mb-2 text-sm font-semibold">Horarios por día</p>
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 p-3 text-sm" data-testid="ally-hours-base">
                  <span className="font-semibold">Horario base</span>
                  <input type="time" value={baseOpen} onChange={(e) => setBaseOpen(e.target.value)}
                    className="rounded-lg border border-slate-200 px-2 py-1" data-testid="ally-hours-base-open" />
                  <span>–</span>
                  <input type="time" value={baseClose} onChange={(e) => setBaseClose(e.target.value)}
                    className="rounded-lg border border-slate-200 px-2 py-1" data-testid="ally-hours-base-close" />
                  <button type="button" onClick={applyHoursToAllWeek}
                    className="rounded-lg bg-[#0B2D4D] px-3 py-1 font-semibold text-white hover:opacity-90"
                    data-testid="ally-hours-apply-all">
                    Aplicar a toda la semana
                  </button>
                  <span className="w-full text-xs text-slate-500">Luego puedes editar cualquier día o marcarlo como “Cerrado”.</span>
                </div>
                <div className="space-y-2">
                  {VET_DAYS.map(([key, label]) => {
                    const h = (form.opening_hours && form.opening_hours[key]) || { open: "", close: "", closed: false };
                    const setDay = (field, val) =>
                      setForm((f) => ({
                        ...f,
                        opening_hours: { ...(f.opening_hours || {}), [key]: { ...h, [field]: val } },
                      }));
                    return (
                      <div key={key} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`ally-hours-${key}`}>
                        <span className="w-20 font-semibold">{label}</span>
                        <label className="flex items-center gap-1">
                          <input type="checkbox" checked={!!h.closed} onChange={(e) => setDay("closed", e.target.checked)} /> Cerrado
                        </label>
                        {!h.closed && (
                          <>
                            <input type="time" value={h.open || ""} onChange={(e) => setDay("open", e.target.value)}
                              className="rounded-lg border border-slate-200 px-2 py-1" data-testid={`ally-hours-${key}-open`} />
                            <span>–</span>
                            <input type="time" value={h.close || ""} onChange={(e) => setDay("close", e.target.value)}
                              className="rounded-lg border border-slate-200 px-2 py-1" data-testid={`ally-hours-${key}-close`} />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <Button className="mt-5 w-full" onClick={submit} disabled={saving || geocoding} data-testid="ally-submit">
          {geocoding ? "Buscando ubicación…" : saving ? "Guardando…" : editId ? "Guardar cambios" : "Crear aliado"}
        </Button>
        </div>
      </div>
    );
  }

  if (mode === "metrics") {
    const d = metrics && metrics.data;
    return (
      <div data-testid="admin-ally-metrics" className="text-white">
        <BackButton onClick={() => setMode("list")} label="Volver a aliados" testId="ally-metrics-back" />
        <h3 className="mt-3 text-lg font-semibold">Métricas · {metrics && metrics.ally.name}</h3>
        {!d ? (
          <p className="mt-4 text-sm text-white/50">Cargando…</p>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ["Vistas", d.views, "views"],
                ["Clics WhatsApp", d.whatsapp_clicks, "wa"],
                ["Clics Instagram", d.instagram_clicks, "ig"],
                ["Clics Website", d.website_clicks, "web"],
              ].map(([label, val, id]) => (
                <div key={id} className="rounded-2xl bg-white/10 p-4" data-testid={`ally-metric-${id}`}>
                  <p className="text-2xl font-bold">{val || 0}</p>
                  <p className="text-xs text-white/60">{label}</p>
                </div>
              ))}
            </div>
            <h4 className="mt-5 text-sm font-semibold text-white/80">Eventos recientes</h4>
            {d.recent && d.recent.length ? (
              <div className="mt-2 space-y-1">
                {d.recent.map((ev, i) => (
                  <div key={i} className="flex justify-between rounded-lg bg-white/5 px-3 py-2 text-xs">
                    <span>{ev.event_type}</span>
                    <span className="text-white/40">{new Date(ev.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-sm text-white/50">Sin eventos aún.</p>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div data-testid="admin-allies" className="text-white">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Aliados PawCita</h3>
        <Button onClick={openCreate} data-testid="ally-create-btn">
          + Nuevo aliado
        </Button>
      </div>
      <div className="mt-3 flex justify-end">
        <CountryFilterSelect
          value={countryFilter}
          onChange={setCountryFilter}
          testId="allies-country-filter"
        />
      </div>
      {err && <p className="mt-2 text-sm text-red-300">{err}</p>}
      {list === null ? (
        <p className="mt-4 text-sm text-white/50">Cargando…</p>
      ) : visibleAllies.length === 0 ? (
        <p className="mt-6 text-sm text-white/60" data-testid="admin-allies-empty">
          Aún no has creado aliados.
        </p>
      ) : (
        <div className="mt-4 space-y-2">
          {visibleAllies.map((a) => {
            const cat = ALLY_CATS.find((c) => c.key === a.category);
            return (
              <div
                key={a.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/10 p-4"
                data-testid={`admin-ally-row-${a.id}`}
              >
                <div className="min-w-0">
                  <p className="font-semibold">
                    {cat ? cat.emoji : "🐾"} {a.name}
                    {!a.active && <span className="ml-2 text-xs text-red-300">(inactivo)</span>}
                  </p>
                  <p className="text-xs text-white/60">
                    {countryLabel(a.country_code)} · {cat ? cat.label : a.category} · {a.city}
                    {a.is_founder ? " · Fundador" : ""}
                    {a.is_verified ? " · Verificado" : ""}
                  </p>

                  {a.is_pawcita_partner ? (
                    <p
                      className="mt-1 text-xs text-emerald-200"
                      data-testid={`ally-access-summary-${a.id}`}
                    >
                      {a.access_status === "active"
                        ? "✓ Panel configurado"
                        : a.access_status === "pending"
                        ? "⏳ Invitación pendiente"
                        : "⚠ Acceso pendiente"}
                      {a.access_email
                        ? ` · ${a.access_email}`
                        : ""}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => openMetrics(a)} data-testid={`ally-metrics-${a.id}`}>
                    Métricas
                  </Button>
                  <Button variant="outline" onClick={() => openEdit(a)} data-testid={`ally-edit-${a.id}`}>
                    Editar
                  </Button>
                  <Button variant="outline" onClick={() => toggleStatus(a)} data-testid={`ally-toggle-${a.id}`}>
                    {a.active ? "Desactivar" : "Activar"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


const EMPTY_ACTIVITY = {
  title: "",
  category: "pet_date",
  short_description: "",
  date: "",
  time: "",
  country_code: "PA",
  city: "",
  location: "",
  price: "",
  capacity: "",
  requirements: "",
  organizer_name: "",
  active: true,
};


const CONTENT_MEDIA_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const contentMediaUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (!CONTENT_MEDIA_BASE) return value;
  return `${CONTENT_MEDIA_BASE}${value.startsWith("/") ? "" : "/"}${value}`;
};

const BANNER_PLACEMENTS = [
  ["home", "Inicio"],
  ["activities", "Actividades"],
  ["allies", "Aliados"],
  ["parks", "Parques"],
  ["adoptions", "Adopciones"],
];

const BANNER_INTERNAL_DESTINATIONS = [
  ["dogs", "Mis perros"],
  ["chats", "Mensajes"],
  ["allies", "Aliados"],
  ["adoptions", "Adopciones"],
  ["sos", "SOS"],
  ["parks", "Parques"],
  ["activities", "Actividades"],
  ["health", "Salud"],
  ["passport", "Pasaporte"],
  ["vet_emergency", "Emergencia veterinaria"],
];

const EMPTY_BANNER = {
  placement: "home",
  countries: ["PA"],
  title: "",
  subtitle: "",
  button_label: "",
  destination_type: "none",
  destination: "",
  sort_order: 0,
  starts_on: "",
  ends_on: "",
};


const BANNER_METRIC_WINDOWS = [
  ["7d", "7 días"],
  ["30d", "30 días"],
  ["total", "Total"],
];

function BannerMetricsPanel({
  data,
  loading,
  windowKey,
  onWindowChange,
}) {
  if (loading) {
    return (
      <div className="border-t border-white/10 bg-black/10 p-4">
        <p className="text-sm text-white/60">
          Cargando métricas…
        </p>
      </div>
    );
  }

  if (!data) return null;

  const metrics =
    data.windows?.[windowKey] || {
      impressions: 0,
      unique_reach: 0,
      clicks: 0,
      unique_clickers: 0,
      ctr: 0,
    };

  const cards = [
    ["Impresiones", metrics.impressions ?? 0],
    ["Alcance", metrics.unique_reach ?? 0],
    ["Clics", metrics.clicks ?? 0],
    [
      "Usuarios con clic",
      metrics.unique_clickers ?? 0,
    ],
    [
      "CTR",
      `${Number(metrics.ctr || 0).toFixed(2)}%`,
    ],
  ];

  return (
    <div
      className="border-t border-white/10 bg-black/10 p-4"
      data-testid="banner-metrics-panel"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-black">
            Rendimiento del banner
          </h4>
          <p className="mt-1 text-xs text-white/50">
            Impresiones y clics registrados en la app.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {BANNER_METRIC_WINDOWS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => onWindowChange(value)}
              className={`rounded-full px-3 py-2 text-xs font-black transition ${
                windowKey === value
                  ? "bg-[#67D5B5] text-[#0B2D4D]"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
              data-testid={`banner-metrics-window-${value}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {cards.map(([label, value]) => (
          <div
            key={label}
            className="rounded-2xl bg-white/10 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-wide text-white/50">
              {label}
            </p>
            <p className="mt-1 text-2xl font-black text-white">
              {value}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-5">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h5 className="text-sm font-black">
              Evolución diaria
            </h5>
            <p className="mt-1 text-xs text-white/40">
              Últimos 30 días con actividad registrada.
            </p>
          </div>
        </div>

        {!data.daily?.length ? (
          <div className="mt-3 rounded-2xl bg-white/5 p-4 text-sm text-white/50">
            Aún no hay actividad registrada para este banner.
          </div>
        ) : (
          <div className="mt-3 max-h-72 overflow-auto rounded-2xl bg-white/5">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-[#17384F] text-xs uppercase text-white/50">
                <tr>
                  <th className="p-3">
                    Fecha
                  </th>
                  <th className="p-3 text-right">
                    Impresiones
                  </th>
                  <th className="p-3 text-right">
                    Clics
                  </th>
                  <th className="p-3 text-right">
                    CTR
                  </th>
                </tr>
              </thead>

              <tbody>
                {[...(data.daily || [])]
                  .reverse()
                  .map((row) => {
                    const dailyCtr = row.impressions
                      ? (
                          (row.clicks / row.impressions) *
                          100
                        ).toFixed(2)
                      : "0.00";

                    return (
                      <tr
                        key={row.day}
                        className="border-t border-white/10"
                      >
                        <td className="p-3 font-bold">
                          {row.day}
                        </td>

                        <td className="p-3 text-right">
                          {row.impressions || 0}
                        </td>

                        <td className="p-3 text-right">
                          {row.clicks || 0}
                        </td>

                        <td className="p-3 text-right">
                          {dailyCtr}%
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}



const CONTENT_CARD_CATEGORIES = [
  ["care", "Cuidados"],
  ["healthy_recipes", "Recetas saludables"],
  ["behavior", "Comportamiento"],
  ["grief", "Duelo y acompañamiento"],
];

const EMPTY_CONTENT_CARD = {
  category: "care",
  countries: ["PA"],
  title: "",
  summary: "",
  content: "",
  video_url: "",
  sort_order: 0,
};


function ContentAdmin() {
  const [section, setSection] = useState("banners");

  return (
    <div className="text-white" data-testid="admin-content-hub">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-black">
          Contenido
        </h2>

        <p className="mt-1 text-sm text-white/60">
          Gestiona el contenido que PawCita muestra dinámicamente
          sin necesidad de publicar una nueva versión de la app.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setSection("banners")}
          className={`rounded-full px-4 py-2 text-sm font-black transition ${
            section === "banners"
              ? "bg-[#67D5B5] text-[#0B2D4D]"
              : "bg-white/10 text-white hover:bg-white/20"
          }`}
          data-testid="content-section-banners"
        >
          Banners
        </button>

        <button
          type="button"
          onClick={() => setSection("cards")}
          className={`rounded-full px-4 py-2 text-sm font-black transition ${
            section === "cards"
              ? "bg-[#67D5B5] text-[#0B2D4D]"
              : "bg-white/10 text-white hover:bg-white/20"
          }`}
          data-testid="content-section-cards"
        >
          Consejos y cuidados
        </button>
      </div>

      {section === "banners"
        ? <BannersAdmin embedded />
        : <ContentCardsAdmin />}
    </div>
  );
}


function ContentCardsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_CONTENT_CARD);
  const [editId, setEditId] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [countryFilter, setCountryFilter] = useState("ALL");

  const load = () => {
    setLoading(true);

    api.admin
      .contentCards(countryFilter === "ALL" ? undefined : countryFilter)
      .then((data) => {
        setItems(data || []);
        setErr("");
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [countryFilter]);

  const visibleItems = items.filter((item) =>
    matchesTargetFilter(item, countryFilter)
  );

  const reset = () => {
    setForm(EMPTY_CONTENT_CARD);
    setEditId(null);
    setExistingImage("");
    setImageFile(null);
    setErr("");
    setNotice("");
  };

  const edit = (item) => {
    setEditId(item.id);
    setExistingImage(item.image || "");
    setImageFile(null);
    setNotice("");
    setErr("");

    setForm({
      category: item.category || "care",
      countries: effectiveTargetCountries(item),
      title: item.title || "",
      summary: item.summary || "",
      content: item.content || "",
      video_url: item.video_url || "",
      sort_order: item.sort_order || 0,
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.countries.length) { setErr("Selecciona al menos un país."); return; }

    if (!form.title.trim()) {
      setErr("Escribe un título.");
      return;
    }

    if (!form.summary.trim()) {
      setErr("Escribe un resumen.");
      return;
    }

    if (!form.content.trim()) {
      setErr("Escribe el contenido completo.");
      return;
    }

    setSaving(true);
    setErr("");
    setNotice("");

    try {
      const payload = {
        ...form,
        sort_order: Number(form.sort_order || 0),
      };

      let saved;

      if (editId) {
        saved = await api.admin.updateContentCard(
          editId,
          payload
        );
      } else {
        saved = await api.admin.createContentCard(
          payload
        );
      }

      if (imageFile) {
        await api.admin.uploadContentCardPhoto(
          saved.id,
          imageFile
        );
      }

      setForm(EMPTY_CONTENT_CARD);
      setEditId(null);
      setExistingImage("");
      setImageFile(null);
      setErr("");
      setNotice(
        editId
          ? "Contenido actualizado."
          : "Contenido creado como borrador."
      );

      load();
    } catch (e2) {
      setErr(
        e2.message ||
          "No se pudo guardar el contenido."
      );
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (item) => {
    try {
      setErr("");
      setNotice("");

      await api.admin.setContentCardStatus(
        item.id,
        !item.active
      );

      setNotice(
        item.active
          ? "Contenido retirado de la app."
          : "Contenido publicado en la app."
      );

      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  const remove = async (item) => {
    const ok = window.confirm(
      `¿Eliminar "${item.title}"? Esta acción no se puede deshacer.`
    );

    if (!ok) return;

    try {
      setErr("");
      setNotice("");

      await api.admin.deleteContentCard(item.id);

      if (editId === item.id) {
        reset();
      }

      setNotice("Contenido eliminado.");
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  const removePhoto = async () => {
    if (!editId || !existingImage) return;

    const ok = window.confirm(
      "¿Quitar la imagen de esta ficha?"
    );

    if (!ok) return;

    try {
      setErr("");
      await api.admin.deleteContentCardPhoto(editId);
      setExistingImage("");
      setNotice("Imagen eliminada.");
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div data-testid="admin-content-cards">
      <div className="mb-6">
        <h3 className="font-display text-xl font-black">
          Consejos y cuidados
        </h3>

        <p className="mt-1 text-sm text-white/60">
          Crea, edita y publica fichas informativas que
          aparecerán automáticamente en Recursos PawCita.
        </p>
      </div>

      <form
        onSubmit={save}
        className="rounded-3xl bg-white/10 p-5"
        data-testid="content-card-form"
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h4 className="font-display text-xl font-black">
              {editId
                ? "Editar contenido"
                : "Nueva ficha"}
            </h4>

            <p className="mt-1 text-xs text-white/50">
              Las fichas nuevas se guardan como borrador.
              Tú decides cuándo publicarlas.
            </p>
          </div>

          {editId && (
            <button
              type="button"
              onClick={reset}
              className="rounded-full bg-white/10 px-4 py-2 text-sm font-bold hover:bg-white/20"
            >
              Cancelar
            </button>
          )}
        </div>

        {err && (
          <p className="mb-4 rounded-2xl bg-rose-500/20 p-3 text-sm text-rose-100">
            {err}
          </p>
        )}

        {notice && (
          <p className="mb-4 rounded-2xl bg-emerald-400/20 p-3 text-sm text-emerald-100">
            {notice}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-bold">
              Categoría *
            </span>

            <select
              value={form.category}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  category: e.target.value,
                }))
              }
              className="w-full rounded-2xl border border-white/20 bg-[#163954] px-4 py-3 text-white outline-none focus:border-[#67D5B5]"
              data-testid="content-card-category"
            >
              {CONTENT_CARD_CATEGORIES.map(
                ([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                )
              )}
            </select>
          </label>

          <div className="block">
            <span className="mb-1.5 block text-sm font-bold">
              Mercado *
            </span>
            <MarketTargets countries={form.countries} testId="content-card-countries"
              onChange={countries => setForm(f => ({ ...f, countries }))} />
            <p className="mt-2 text-xs text-white/40">
              Define en qué país o países se publicará esta ficha.
            </p>
          </div>

          <label className="block">
            <span className="mb-1.5 block text-sm font-bold">
              Orden
            </span>

            <input
              type="number"
              min="0"
              step="1"
              value={form.sort_order}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  sort_order: e.target.value,
                }))
              }
              className="w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white outline-none focus:border-[#67D5B5]"
              data-testid="content-card-sort-order"
            />
          </label>

          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-sm font-bold">
              Título *
            </span>

            <input
              value={form.title}
              maxLength={120}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  title: e.target.value,
                }))
              }
              placeholder="Ej. 5 alimentos seguros para tu perro"
              className="w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white outline-none placeholder-white/30 focus:border-[#67D5B5]"
              data-testid="content-card-title"
            />

            <p className="mt-1 text-right text-xs text-white/35">
              {form.title.length}/120
            </p>
          </label>

          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-sm font-bold">
              Resumen *
            </span>

            <textarea
              value={form.summary}
              maxLength={320}
              rows={3}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  summary: e.target.value,
                }))
              }
              placeholder="Una introducción breve que aparecerá en la tarjeta."
              className="w-full resize-none rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white outline-none placeholder-white/30 focus:border-[#67D5B5]"
              data-testid="content-card-summary"
            />

            <p className="mt-1 text-right text-xs text-white/35">
              {form.summary.length}/320
            </p>
          </label>

          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-sm font-bold">
              Contenido completo *
            </span>

            <textarea
              value={form.content}
              maxLength={20000}
              rows={12}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  content: e.target.value,
                }))
              }
              placeholder={
                "Escribe aquí la información completa. Puedes usar párrafos y saltos de línea."
              }
              className="w-full resize-y rounded-2xl border border-white/20 bg-white/10 px-4 py-3 leading-6 text-white outline-none placeholder-white/30 focus:border-[#67D5B5]"
              data-testid="content-card-content"
            />

            <p className="mt-1 flex justify-between gap-3 text-xs text-white/35">
              <span>
                Se mostrará al tocar “Ver más”.
              </span>
              <span>
                {form.content.length}/20,000
              </span>
            </p>
          </label>

          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-sm font-bold">
              Video de YouTube
              <span className="ml-2 font-normal text-white/40">
                Opcional
              </span>
            </span>

            <input
              type="url"
              value={form.video_url}
              maxLength={500}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  video_url: e.target.value,
                }))
              }
              placeholder="Pega aquí el enlace de YouTube o YouTube Shorts"
              className="w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white outline-none placeholder-white/30 focus:border-[#67D5B5]"
              data-testid="content-card-video-url"
            />

            <p className="mt-2 text-xs leading-5 text-white/40">
              Acepta videos normales, Shorts y enlaces compartidos de YouTube.
              Si dejas este campo vacío, la ficha funcionará solo con imagen y texto.
            </p>
          </label>

          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-sm font-bold">
              Imagen
            </span>

            {existingImage && (
              <div className="mb-3 overflow-hidden rounded-2xl bg-white/5">
                <img
                  src={contentMediaUrl(existingImage)}
                  alt="Imagen actual del contenido"
                  className="aspect-[16/9] w-full object-cover"
                />

                {editId && (
                  <div className="border-t border-white/10 p-3">
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="rounded-full bg-rose-500/20 px-4 py-2 text-xs font-black text-rose-100 hover:bg-rose-500/30"
                    >
                      Quitar imagen
                    </button>
                  </div>
                )}
              </div>
            )}

            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) =>
                setImageFile(
                  e.target.files?.[0] || null
                )
              }
              className="block w-full rounded-2xl border border-white/20 bg-white/10 p-3 text-sm text-white"
              data-testid="content-card-image"
            />

            {imageFile && (
              <p className="mt-2 text-xs text-emerald-200">
                Nueva imagen: {imageFile.name}
              </p>
            )}

            <p className="mt-2 text-xs text-white/40">
              JPG, PNG o WEBP. Máximo 5 MB.
              Recomendado: imagen horizontal.
            </p>
          </label>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="mt-5 rounded-full bg-[#67D5B5] px-6 py-3 font-black text-[#0B2D4D] disabled:opacity-50"
          data-testid="content-card-save"
        >
          {saving
            ? "Guardando…"
            : editId
              ? "Guardar cambios"
              : "Crear borrador"}
        </button>
      </form>

      <div className="mt-8">
        <div className="mb-4 flex items-end justify-between gap-3">
          <div>
            <h4 className="font-display text-xl font-black">
              Fichas creadas
            </h4>

            <p className="mt-1 text-xs text-white/50">
              Solo las publicadas aparecen en Recursos PawCita.
            </p>
          </div>

          <button
            type="button"
            onClick={load}
            className="rounded-full bg-white/10 px-4 py-2 text-sm font-bold hover:bg-white/20"
          >
            Actualizar
          </button>
        </div>

        <div className="mb-4 flex justify-end">
          <CountryFilterSelect
            value={countryFilter}
            onChange={setCountryFilter}
            testId="content-cards-country-filter"
          />
        </div>

        {loading ? (
          <div className="rounded-3xl bg-white/5 p-6 text-sm text-white/60">
            Cargando contenido…
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/20 bg-white/5 p-8 text-center">
            <p className="font-display text-lg font-black">
              Aún no hay fichas
            </p>

            <p className="mt-2 text-sm text-white/50">
              Crea la primera para empezar a poblar Recursos PawCita.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {visibleItems.map((item) => (
              <div
                key={item.id}
                className="overflow-hidden rounded-3xl bg-white/10"
                data-testid={`content-card-row-${item.id}`}
              >
                <div className="grid gap-4 p-4 sm:grid-cols-[130px_1fr]">
                  <div className="overflow-hidden rounded-2xl bg-white/5">
                    {item.image ? (
                      <img
                        src={contentMediaUrl(item.image)}
                        alt=""
                        className="aspect-[4/3] h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex aspect-[4/3] items-center justify-center text-3xl">
                        🐾
                      </div>
                    )}
                  </div>

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-black text-white/70">
                        {item.category_label}
                      </span>

                      <span className="rounded-full bg-sky-300/20 px-3 py-1 text-xs font-black text-sky-100">
                        {targetCountriesLabel(item)}
                      </span>

                      <span
                        className={`rounded-full px-3 py-1 text-xs font-black ${
                          item.active
                            ? "bg-emerald-300 text-[#0B2D4D]"
                            : "bg-white/10 text-white/60"
                        }`}
                      >
                        {item.active
                          ? "Publicado"
                          : "Borrador"}
                      </span>

                      <span className="text-xs text-white/40">
                        Orden {item.sort_order || 0}
                      </span>

                      {item.video_id && (
                        <span className="rounded-full bg-red-500/20 px-3 py-1 text-xs font-black text-red-100">
                          ▶ Video
                        </span>
                      )}
                    </div>

                    <h5 className="mt-3 font-display text-lg font-black">
                      {item.title}
                    </h5>

                    <p className="mt-1 line-clamp-2 text-sm leading-5 text-white/60">
                      {item.summary}
                    </p>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => edit(item)}
                        className="rounded-full bg-white/10 px-4 py-2 text-xs font-black hover:bg-white/20"
                      >
                        Editar
                      </button>

                      <button
                        type="button"
                        onClick={() => toggle(item)}
                        className={`rounded-full px-4 py-2 text-xs font-black ${
                          item.active
                            ? "bg-amber-300 text-[#0B2D4D]"
                            : "bg-[#67D5B5] text-[#0B2D4D]"
                        }`}
                      >
                        {item.active
                          ? "Retirar"
                          : "Publicar"}
                      </button>

                      <button
                        type="button"
                        onClick={() => remove(item)}
                        className="rounded-full bg-rose-500/20 px-4 py-2 text-xs font-black text-rose-100 hover:bg-rose-500/30"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function BannersAdmin({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState({
    allies: [],
    activities: [],
    organizations: [],
  });
  const [form, setForm] = useState(EMPTY_BANNER);
  const [editId, setEditId] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [metricsOpenId, setMetricsOpenId] = useState(null);
  const [metricsById, setMetricsById] = useState({});
  const [metricsLoadingId, setMetricsLoadingId] = useState(null);
  const [metricsWindow, setMetricsWindow] = useState("7d");
  const [countryFilter, setCountryFilter] = useState("ALL");

  const destinationCountry = form.countries.length === 1 ? form.countries[0] : null;

  useEffect(() => {
    let cancelled = false;
    setDestinationOptions({ allies: [], activities: [], organizations: [] });
    if (!destinationCountry) return;

    Promise.allSettled([
      api.admin.allies("", destinationCountry),
      api.admin.activities(destinationCountry),
      api.admin.organizations("", destinationCountry),
    ]).then(
      ([
        alliesResult,
        activitiesResult,
        organizationsResult,
      ]) => {
        if (cancelled) return;
        setDestinationOptions({
          allies:
            alliesResult.status === "fulfilled"
              ? alliesResult.value || []
              : [],
          activities:
            activitiesResult.status === "fulfilled"
              ? activitiesResult.value || []
              : [],
          organizations:
            organizationsResult.status === "fulfilled"
              ? organizationsResult.value || []
              : [],
        });
      }
    );
    return () => { cancelled = true; };
  }, [destinationCountry]);

  const load = () => {
    setLoading(true);

    api.admin
      .banners(countryFilter === "ALL" ? undefined : countryFilter)
      .then((data) => {
        setItems(data || []);
        setErr("");
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [countryFilter]);

  const reset = () => {
    setForm(EMPTY_BANNER);
    setEditId(null);
    setExistingImage("");
    setImageFile(null);
    setErr("");
    setNotice("");
  };

  const edit = (banner) => {
    setEditId(banner.id);
    setExistingImage(banner.image || "");
    setImageFile(null);
    setNotice("");
    setErr("");

    setForm({
      placement: banner.placement || "home",
      countries: effectiveTargetCountries(banner),
      title: banner.title || "",
      subtitle: banner.subtitle || "",
      button_label: banner.button_label || "",
      destination_type: banner.destination_type || "none",
      destination: banner.destination || "",
      sort_order: banner.sort_order || 0,
      starts_on: banner.starts_on || "",
      ends_on: banner.ends_on || "",
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.countries.length) { setErr("Selecciona al menos un país."); return; }

    if (!form.title.trim()) {
      setErr("Escribe un título para el banner.");
      return;
    }

    if (form.destination_type !== "none" && !form.destination) {
      setErr("Selecciona o escribe el destino del banner.");
      return;
    }

    setSaving(true);
    setErr("");
    setNotice("");

    try {
      const payload = {
        ...form,
        sort_order: Number(form.sort_order || 0),
      };

      let saved;

      if (editId) {
        saved = await api.admin.updateBanner(editId, payload);
      } else {
        saved = await api.admin.createBanner(payload);
      }

      if (imageFile) {
        await api.admin.uploadBannerPhoto(saved.id, imageFile);
      }

      const message = editId ? "Banner actualizado." : "Banner creado.";

      setForm(EMPTY_BANNER);
      setEditId(null);
      setExistingImage("");
      setImageFile(null);
      setErr("");
      setNotice(message);

      load();
    } catch (e2) {
      setErr(e2.message || "No se pudo guardar el banner.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (banner) => {
    try {
      await api.admin.setBannerStatus(banner.id, !banner.active);
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  const remove = async (banner) => {
    const ok = window.confirm(
      `¿Eliminar el banner "${banner.title}"? Esta acción no se puede deshacer.`
    );

    if (!ok) return;

    try {
      await api.admin.deleteBanner(banner.id);

      if (editId === banner.id) {
        reset();
      }

      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  const openMetrics = async (banner) => {
    if (metricsOpenId === banner.id) {
      setMetricsOpenId(null);
      return;
    }

    setMetricsOpenId(banner.id);
    setMetricsWindow("7d");
    setMetricsLoadingId(banner.id);
    setErr("");

    try {
      const data = await api.admin.bannerMetrics(
        banner.id
      );

      setMetricsById((current) => ({
        ...current,
        [banner.id]: data,
      }));
    } catch (e) {
      setErr(
        e.message ||
          "No se pudieron cargar las métricas del banner."
      );
    } finally {
      setMetricsLoadingId(null);
    }
  };


  const visibleBanners = items.filter((banner) =>
    matchesTargetFilter(banner, countryFilter)
  );
  const availableDestinationOptions = {
    allies: destinationCountry
      ? destinationOptions.allies.filter((item) =>
          matchesCountryFilter(item, destinationCountry)
        )
      : [],
    activities: destinationCountry
      ? destinationOptions.activities.filter((item) =>
          matchesCountryFilter(item, destinationCountry)
        )
      : [],
    organizations: destinationCountry
      ? destinationOptions.organizations.filter((item) =>
          matchesCountryFilter(item, destinationCountry)
        )
      : [],
  };

  const changeDestinationType = (value) => {
    setForm((f) => ({
      ...f,
      destination_type: value,
      destination: "",
      button_label: value === "none" ? "" : f.button_label,
    }));
  };

  return (
    <div className="text-white" data-testid="admin-content">
      {!embedded && (
        <div className="mb-6">
          <h2 className="font-display text-2xl font-black">
            Contenido
          </h2>

          <p className="mt-1 text-sm text-white/60">
            Administra los banners que aparecen en las distintas secciones de PawCita.
          </p>
        </div>
      )}

      {embedded && (
        <div className="mb-6">
          <h3 className="font-display text-xl font-black">
            Banners
          </h3>

          <p className="mt-1 text-sm text-white/60">
            Administra las campañas visuales que aparecen en las distintas secciones de PawCita.
          </p>
        </div>
      )}

      <form
        onSubmit={save}
        className="rounded-3xl bg-white/10 p-5"
        data-testid="admin-banner-form"
      >
        <div className="mb-5 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-black">
              {editId ? "Editar banner" : "Nuevo banner"}
            </h3>

            <p className="mt-1 text-xs text-white/50">
              Elige dónde aparece, prográmalo, ordénalo y define qué ocurre al tocarlo.
            </p>
          </div>

          {editId && (
            <button
              type="button"
              onClick={reset}
              className="rounded-full bg-white/10 px-4 py-2 text-sm font-bold hover:bg-white/20"
            >
              Cancelar
            </button>
          )}
        </div>

        {err && (
          <p className="mb-4 rounded-2xl bg-rose-500/20 p-3 text-sm text-rose-100">
            {err}
          </p>
        )}

        {notice && (
          <p className="mb-4 rounded-2xl bg-emerald-400/20 p-3 text-sm text-emerald-100">
            {notice}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-sm font-bold">
              Ubicación del banner *
            </span>

            <select
              value={form.placement}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  placement: e.target.value,
                }))
              }
              className="w-full rounded-2xl border border-white/20 bg-[#163954] px-4 py-3 text-white outline-none focus:border-[#67D5B5]"
              data-testid="banner-placement"
            >
              {BANNER_PLACEMENTS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <p className="mt-2 text-xs text-white/40">
              Define en qué pantalla aparecerá esta campaña.
            </p>
          </label>

          <div className="block sm:col-span-2">
            <span className="mb-1.5 block text-sm font-bold">
              Mercado *
            </span>
            <MarketTargets countries={form.countries} testId="banner-countries"
              onChange={countries => setForm(f => ({
                ...f, countries,
                ...(["ally", "activity", "organization", "adoption"].includes(f.destination_type)
                  ? { destination_type: "none", destination: "", button_label: "" } : {}),
              }))} />
            <p className="mt-2 text-xs text-white/40">
              Los banners históricos sin mercado se consideran Panamá.
            </p>
          </div>

          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-sm font-bold">
              Título interno / accesibilidad *
            </span>

            <input
              value={form.title}
              onChange={(e) =>
                setForm((f) => ({ ...f, title: e.target.value }))
              }
              maxLength={140}
              placeholder="Ej. Aliados cerca de ti"
              className="w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white outline-none placeholder-white/30 focus:border-[#67D5B5]"
              data-testid="banner-title"
            />
          </label>

          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-sm font-bold">
              Texto secundario
            </span>

            <textarea
              value={form.subtitle}
              onChange={(e) =>
                setForm((f) => ({ ...f, subtitle: e.target.value }))
              }
              maxLength={400}
              rows={3}
              placeholder="Una frase breve para acompañar el banner."
              className="w-full resize-none rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white outline-none placeholder-white/30 focus:border-[#67D5B5]"
              data-testid="banner-subtitle"
            />
          </label>

          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-sm font-bold">
              Creatividad del banner
            </span>

            {existingImage && (
              <div className="mb-3 overflow-hidden rounded-2xl bg-white/5">
                <img
                  src={contentMediaUrl(existingImage)}
                  alt="Banner actual"
                  className="aspect-[12/5] w-full object-cover"
                />
              </div>
            )}

            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) =>
                setImageFile(e.target.files?.[0] || null)
              }
              className="block w-full rounded-2xl border border-white/20 bg-white/10 p-3 text-sm text-white"
              data-testid="banner-image"
            />

            {imageFile && (
              <p className="mt-2 text-xs text-emerald-200">
                Nueva imagen: {imageFile.name}
              </p>
            )}

            <p className="mt-2 text-xs text-white/40">
              JPG, PNG o WEBP. Máximo 5 MB. Recomendado: 1200 × 500 px.
            </p>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-bold">
              Acción al tocar
            </span>

            <select
              value={form.destination_type}
              onChange={(e) => changeDestinationType(e.target.value)}
              className="w-full rounded-2xl border border-white/20 bg-[#163954] px-4 py-3 text-white outline-none focus:border-[#67D5B5]"
              data-testid="banner-destination-type"
            >
              <option value="none">Sin acción</option>
              <option value="internal">Sección de PawCita</option>
              <option value="ally" disabled={!destinationCountry}>
                Aliado específico
              </option>
              <option value="activity" disabled={!destinationCountry}>
                Actividad específica
              </option>
              <option value="organization" disabled={!destinationCountry}>
                Organización específica
              </option>
              <option value="external">Enlace externo</option>
            </select>
            {!destinationCountry && (
              <p className="mt-2 text-xs text-white/40">
                Para un banner visible en varios países, usa una sección de
                PawCita o un enlace externo. Los destinos específicos pertenecen
                a un solo mercado.
              </p>
            )}
          </label>

          {form.destination_type !== "none" && (
            <label className="block">
              <span className="mb-1.5 block text-sm font-bold">
                Texto del botón
              </span>

              <input
                value={form.button_label}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    button_label: e.target.value,
                  }))
                }
                maxLength={50}
                placeholder="Ej. Ver más"
                className="w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white outline-none placeholder-white/30 focus:border-[#67D5B5]"
                data-testid="banner-button-label"
              />
            </label>
          )}

          {form.destination_type === "internal" && (
            <label className="block sm:col-span-2">
              <span className="mb-1.5 block text-sm font-bold">
                Sección de PawCita
              </span>

              <select
                value={form.destination}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    destination: e.target.value,
                  }))
                }
                className="w-full rounded-2xl border border-white/20 bg-[#163954] px-4 py-3 text-white outline-none focus:border-[#67D5B5]"
                data-testid="banner-internal-destination"
              >
                <option value="">Seleccionar sección…</option>

                {BANNER_INTERNAL_DESTINATIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {form.destination_type === "ally" && (
            <label className="block sm:col-span-2">
              <span className="mb-1.5 block text-sm font-bold">
                Aliado
              </span>

              <select
                value={form.destination}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    destination: e.target.value,
                  }))
                }
                className="w-full rounded-2xl border border-white/20 bg-[#163954] px-4 py-3 text-white outline-none focus:border-[#67D5B5]"
                data-testid="banner-ally-destination"
              >
                <option value="">Seleccionar aliado…</option>

                {availableDestinationOptions.allies.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name || "Aliado sin nombre"}
                  </option>
                ))}
              </select>

              {availableDestinationOptions.allies.length === 0 && (
                <p className="mt-2 text-xs text-white/40">
                  No hay aliados disponibles para seleccionar.
                </p>
              )}
            </label>
          )}

          {form.destination_type === "activity" && (
            <label className="block sm:col-span-2">
              <span className="mb-1.5 block text-sm font-bold">
                Actividad
              </span>

              <select
                value={form.destination}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    destination: e.target.value,
                  }))
                }
                className="w-full rounded-2xl border border-white/20 bg-[#163954] px-4 py-3 text-white outline-none focus:border-[#67D5B5]"
                data-testid="banner-activity-destination"
              >
                <option value="">Seleccionar actividad…</option>

                {availableDestinationOptions.activities.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title || "Actividad sin título"}
                  </option>
                ))}
              </select>

              {availableDestinationOptions.activities.length === 0 && (
                <p className="mt-2 text-xs text-white/40">
                  No hay actividades disponibles para seleccionar.
                </p>
              )}
            </label>
          )}

          {form.destination_type === "organization" && (
            <label className="block sm:col-span-2">
              <span className="mb-1.5 block text-sm font-bold">
                Organización
              </span>

              <select
                value={form.destination}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    destination: e.target.value,
                  }))
                }
                className="w-full rounded-2xl border border-white/20 bg-[#163954] px-4 py-3 text-white outline-none focus:border-[#67D5B5]"
                data-testid="banner-organization-destination"
              >
                <option value="">Seleccionar organización…</option>

                {availableDestinationOptions.organizations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.organization_name ||
                      item.name ||
                      "Organización sin nombre"}
                  </option>
                ))}
              </select>

              {availableDestinationOptions.organizations.length === 0 && (
                <p className="mt-2 text-xs text-white/40">
                  No hay organizaciones disponibles para seleccionar.
                </p>
              )}
            </label>
          )}

          {form.destination_type === "external" && (
            <label className="block sm:col-span-2">
              <span className="mb-1.5 block text-sm font-bold">
                Enlace externo
              </span>

              <input
                type="url"
                value={form.destination}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    destination: e.target.value,
                  }))
                }
                placeholder="https://..."
                className="w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white outline-none placeholder-white/30 focus:border-[#67D5B5]"
                data-testid="banner-external-destination"
              />
            </label>
          )}

          <label className="block">
            <span className="mb-1.5 block text-sm font-bold">
              Fecha de inicio
            </span>

            <input
              type="date"
              value={form.starts_on}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  starts_on: e.target.value,
                }))
              }
              className="w-full rounded-2xl border border-white/20 bg-[#163954] px-4 py-3 text-white outline-none focus:border-[#67D5B5]"
              data-testid="banner-starts-on"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-bold">
              Fecha de finalización
            </span>

            <input
              type="date"
              value={form.ends_on}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  ends_on: e.target.value,
                }))
              }
              className="w-full rounded-2xl border border-white/20 bg-[#163954] px-4 py-3 text-white outline-none focus:border-[#67D5B5]"
              data-testid="banner-ends-on"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-bold">
              Orden
            </span>

            <input
              type="number"
              value={form.sort_order}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  sort_order: e.target.value,
                }))
              }
              min="0"
              step="1"
              className="w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-white outline-none focus:border-[#67D5B5]"
              data-testid="banner-sort-order"
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="mt-5 rounded-full bg-[#67D5B5] px-6 py-3 font-black text-[#0B2D4D] disabled:opacity-50"
          data-testid="banner-save"
        >
          {saving
            ? "Guardando…"
            : editId
              ? "Guardar cambios"
              : "Crear banner"}
        </button>
      </form>

      <div className="mt-8">
        <div className="mb-4 flex items-end justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-black">
              Banners creados
            </h3>

            <p className="mt-1 text-xs text-white/50">
              Solo los activos y vigentes aparecerán en la app.
            </p>
          </div>

          <button
            type="button"
            onClick={load}
            className="rounded-full bg-white/10 px-4 py-2 text-sm font-bold hover:bg-white/20"
          >
            Actualizar
          </button>
        </div>

        <div className="mb-4 flex justify-end">
          <CountryFilterSelect
            value={countryFilter}
            onChange={setCountryFilter}
            testId="banners-country-filter"
          />
        </div>

        {loading ? (
          <p className="text-sm text-white/60">Cargando banners…</p>
        ) : visibleBanners.length === 0 ? (
          <div
            className="rounded-3xl bg-white/5 p-6 text-sm text-white/60"
            data-testid="admin-banners-empty"
          >
            Aún no has creado banners.
          </div>
        ) : (
          <div className="space-y-3">
            {visibleBanners.map((banner) => (
              <div
                key={banner.id}
                className="overflow-hidden rounded-3xl bg-white/10"
                data-testid={`admin-banner-${banner.id}`}
              >
                <div className="grid sm:grid-cols-[180px_1fr]">
                  <div className="h-36 bg-white/5 sm:h-full">
                    {banner.image ? (
                      <img
                        src={contentMediaUrl(banner.image)}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full min-h-[140px] items-center justify-center text-3xl">
                        🐾
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-black">
                            {banner.title}
                          </p>

                          <span className="rounded-full bg-sky-300/20 px-2.5 py-1 text-[10px] font-black text-sky-100">
                            {targetCountriesLabel(banner)}
                          </span>

                          <span
                            className={`rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wider ${
                              banner.active
                                ? "bg-emerald-400/20 text-emerald-200"
                                : "bg-white/10 text-white/50"
                            }`}
                          >
                            {banner.active ? "Activo" : "Inactivo"}
                          </span>
                        </div>

                        {banner.subtitle && (
                          <p className="mt-1 text-sm text-white/60">
                            {banner.subtitle}
                          </p>
                        )}

                        <p className="mt-3 text-xs text-white/40">
                          Orden {banner.sort_order ?? 0}
                          {banner.starts_on
                            ? ` · Desde ${banner.starts_on}`
                            : ""}
                          {banner.ends_on
                            ? ` · Hasta ${banner.ends_on}`
                            : ""}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => openMetrics(banner)}
                          className={`rounded-full px-3 py-2 text-xs font-black transition ${
                            metricsOpenId === banner.id
                              ? "bg-[#67D5B5] text-[#0B2D4D]"
                              : "bg-white/10 text-white hover:bg-white/20"
                          }`}
                          data-testid={`banner-metrics-${banner.id}`}
                        >
                          {metricsOpenId === banner.id
                            ? "Cerrar métricas"
                            : "Ver métricas"}
                        </button>

                        <button
                          type="button"
                          onClick={() => edit(banner)}
                          className="rounded-full bg-white/10 px-3 py-2 text-xs font-bold hover:bg-white/20"
                        >
                          Editar
                        </button>

                        <button
                          type="button"
                          onClick={() => toggle(banner)}
                          className="rounded-full bg-white/10 px-3 py-2 text-xs font-bold hover:bg-white/20"
                        >
                          {banner.active ? "Desactivar" : "Activar"}
                        </button>

                        <button
                          type="button"
                          onClick={() => remove(banner)}
                          className="rounded-full bg-rose-500/20 px-3 py-2 text-xs font-bold text-rose-200 hover:bg-rose-500/30"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {metricsOpenId === banner.id && (
                  <BannerMetricsPanel
                    data={metricsById[banner.id]}
                    loading={
                      metricsLoadingId === banner.id
                    }
                    windowKey={metricsWindow}
                    onWindowChange={setMetricsWindow}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function ActivitiesAdmin() {
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("list");
  const [form, setForm] = useState(EMPTY_ACTIVITY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [parts, setParts] = useState(null); // {activity, data}
  const [chatFor, setChatFor] = useState(null); // activity
  const [countryFilter, setCountryFilter] = useState("ALL");

  const uploadPoster = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !editId) return;
    setSaving(true);
    setErr("");
    try {
      const r = await api.admin.uploadActivityPhoto(editId, file);
      setForm((f) => ({ ...f, image: r.image }));
    } catch (err) {
      setErr(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openParticipants = (a) => {
    setParts({ activity: a, data: null });
    setMode("participants");
    api.admin
      .activityParticipants(a.id)
      .then((d) => setParts({ activity: a, data: d }))
      .catch((e) => setErr(e.message));
  };

  const load = () => {
    setErr("");
    api.admin.activities(countryFilter === "ALL" ? undefined : countryFilter).then(setList).catch((e) => setErr(e.message));
  };
  useEffect(load, [countryFilter]);

  const openCreate = () => {
    setForm(EMPTY_ACTIVITY);
    setEditId(null);
    setMode("form");
  };
  const openEdit = (a) => {
    setForm({ ...EMPTY_ACTIVITY, ...a });
    setEditId(a.id);
    setMode("form");
  };
  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async () => {
    setErr("");
    if (!form.title.trim() || !form.short_description.trim() || !form.date || !form.time || !form.city.trim() || !form.location.trim()) {
      setErr("Título, descripción, fecha, hora, ciudad y ubicación son obligatorios.");
      return;
    }
    setSaving(true);
    try {
      if (editId) await api.admin.updateActivity(editId, form);
      else await api.admin.createActivity(form);
      setMode("list");
      load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const visibleActivities = (list || []).filter((activity) =>
    matchesCountryFilter(activity, countryFilter)
  );

  const toggleStatus = async (a) => {
    try {
      await api.admin.setActivityStatus(a.id, !a.active);
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  if (chatFor) {
    return (
      <div className="h-[70vh]" data-testid="admin-activity-chat">
        <ActivityChat activityId={chatFor.id} title={chatFor.title} isAdmin onBack={() => setChatFor(null)} />
      </div>
    );
  }

  if (mode === "participants") {
    const d = parts && parts.data;
    return (
      <div data-testid="admin-activity-participants" className="text-white">
        <BackButton onClick={() => setMode("list")} label="Volver a actividades" testId="participants-back" />
        <h3 className="mt-3 text-lg font-semibold">Participantes · {parts && parts.activity.title}</h3>
        {!d ? (
          <p className="mt-4 text-sm text-white/50">Cargando…</p>
        ) : (
          <>
            <p className="mt-2 text-sm text-white/70">{d.active} activos · {d.total} en total</p>
            {d.participants.length === 0 ? (
              <p className="mt-4 text-sm text-white/60" data-testid="participants-empty">Aún no hay inscritos.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {d.participants.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-2xl bg-white/10 p-4" data-testid={`participant-row-${p.id}`}>
                    <div className="min-w-0">
                      <p className="font-semibold">{p.user_name || "—"} · 🐶 {p.dog_name || "—"}</p>
                      <p className="text-xs text-white/60">{p.created_at ? new Date(p.created_at).toLocaleString() : ""}</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${p.status === "active" ? "bg-emerald-400/20 text-emerald-200" : "bg-rose-400/20 text-rose-200"}`}>
                      {p.status === "active" ? "Activo" : "Cancelado"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  if (mode === "form") {
    return (
      <div data-testid="admin-activity-form" className="text-white">
        <BackButton onClick={() => setMode("list")} label="Volver a actividades" testId="activity-form-back" />
        <h3 className="mt-3 text-lg font-semibold">{editId ? "Editar actividad" : "Nueva actividad"}</h3>
        {err && <p className="mt-2 text-sm text-red-300" data-testid="activity-form-error">{err}</p>}
        <div className="mt-4 rounded-3xl bg-white p-4 text-[#0B2D4D] sm:p-5">
          <div className="mb-4">
            <p className="mb-1.5 text-sm font-semibold">Poster / imagen</p>
            {editId ? (
              <div className="flex items-center gap-3">
                <div className="flex h-16 w-24 items-center justify-center overflow-hidden rounded-2xl bg-slate-100">
                  {form.image ? (
                    <img src={form.image} alt="poster" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-2xl">🖼️</span>
                  )}
                </div>
                <label className="cursor-pointer rounded-full bg-[#0B2D4D] px-4 py-2 text-sm font-bold text-white">
                  {saving ? "Subiendo…" : form.image ? "Reemplazar imagen" : "Subir imagen"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPoster} data-testid="activity-photo-input" />
                </label>
              </div>
            ) : (
              <p className="text-xs text-slate-500">Guarda la actividad primero para subir su poster.</p>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Título *" value={form.title} onChange={set("title")} testId="activity-title" />
            <Select label="Categoría *" value={form.category} onChange={set("category")} testId="activity-category">
              {ACTIVITY_CATS.map((c) => (
                <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>
              ))}
            </Select>
            <Select
              label="País *"
              value={form.country_code || "PA"}
              onChange={(e) =>
                setForm((current) => ({
                  ...current,
                  country_code: e.target.value,
                  city: "",
                }))
              }
              testId="activity-country"
            >
              {COUNTRY_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Input label="Fecha *" type="date" value={form.date} onChange={set("date")} testId="activity-date" />
            <Input label="Hora *" type="time" value={form.time} onChange={set("time")} testId="activity-time" />
            <CityInput
              countryCode={form.country_code || "PA"}
              value={form.city}
              onChange={(value) =>
                setForm((current) => ({ ...current, city: value }))
              }
              label="Ciudad / zona *"
              testId="activity-city"
            />
            <Input label="Ubicación *" value={form.location} onChange={set("location")} testId="activity-location" />
            <Input label="Precio" value={form.price} onChange={set("price")} testId="activity-price" />
            <Input label="Cupos (informativo)" value={form.capacity} onChange={set("capacity")} testId="activity-capacity" />
            <Input label="Organizador" value={form.organizer_name} onChange={set("organizer_name")} testId="activity-organizer" />
          </div>
          <div className="mt-3 grid gap-3">
            <Input label="Descripción corta *" value={form.short_description} onChange={set("short_description")} testId="activity-short" />
            <Textarea label="Requisitos" value={form.requirements} onChange={set("requirements")} testId="activity-requirements" rows={3} />
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm" data-testid="activity-active-toggle">
            <input type="checkbox" checked={form.active} onChange={set("active")} /> Activa
          </label>
          <Button className="mt-5 w-full" onClick={submit} disabled={saving} data-testid="activity-submit">
            {saving ? "Guardando…" : editId ? "Guardar cambios" : "Crear actividad"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-activities" className="text-white">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Actividades PawCita</h3>
        <Button onClick={openCreate} data-testid="activity-create-btn">+ Nueva actividad</Button>
      </div>
      <div className="mt-3 flex justify-end">
        <CountryFilterSelect
          value={countryFilter}
          onChange={setCountryFilter}
          testId="activities-country-filter"
        />
      </div>
      {err && <p className="mt-2 text-sm text-red-300">{err}</p>}
      {list === null ? (
        <p className="mt-4 text-sm text-white/50">Cargando…</p>
      ) : visibleActivities.length === 0 ? (
        <p className="mt-6 text-sm text-white/60" data-testid="admin-activities-empty">Aún no has creado actividades.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {visibleActivities.map((a) => {
            const cat = ACTIVITY_CATS.find((c) => c.key === a.category);
            return (
              <div
                key={a.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/10 p-4"
                data-testid={`admin-activity-row-${a.id}`}
              >
                <div className="min-w-0">
                  <p className="font-semibold">
                    {cat ? cat.emoji : "🐾"} {a.title}
                    {!a.active && <span className="ml-2 text-xs text-red-300">(inactiva)</span>}
                  </p>
                  <p className="text-xs text-white/60">
                    {countryLabel(a.country_code)} · {cat ? cat.label : a.category} · {a.date} {a.time} · {a.city}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => setChatFor(a)} data-testid={`activity-chat-${a.id}`}>Chat grupal</Button>
                  <Button variant="outline" onClick={() => openParticipants(a)} data-testid={`activity-participants-${a.id}`}>Participantes</Button>
                  <Button variant="outline" onClick={() => openEdit(a)} data-testid={`activity-edit-${a.id}`}>Editar</Button>
                  <Button variant="outline" onClick={() => toggleStatus(a)} data-testid={`activity-toggle-${a.id}`}>
                    {a.active ? "Desactivar" : "Activar"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


const SOS_FILTERS = [
  { key: "active", label: "Activas" },
  { key: "found", label: "Encontradas" },
  { key: "closed", label: "Cerradas" },
];

function SosAdmin() {
  const [filter, setFilter] = useState("active");
  const [list, setList] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sightings, setSightings] = useState(null);
  const [err, setErr] = useState("");

  const loadSightings = (alertId) => {
    setSightings(null);
    api.admin.sosSightings(alertId).then(setSightings).catch(() => setSightings([]));
  };

  const openDetail = (a) => {
    setDetail(a);
    loadSightings(a.id);
  };

  const toggleSighting = async (s) => {
    const next = s.status === "removed" ? "active" : "removed";
    try {
      await api.admin.setSosSightingStatus(s.id, next);
      loadSightings(detail.id);
    } catch (e) {
      setErr(e.message);
    }
  };

  const load = () => {
    setErr("");
    setList(null);
    api.admin
      .sosAlerts(filter)
      .then(setList)
      .catch((e) => setErr(e.message));
  };
  useEffect(load, [filter]);

  const changeStatus = async (a, status) => {
    try {
      const updated = await api.admin.setSosStatus(a.id, status);
      setDetail(updated);
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  if (detail) {
    return (
      <div data-testid="admin-sos-detail" className="text-white">
        <BackButton onClick={() => setDetail(null)} label="Volver a SOS" testId="sos-admin-detail-back" />
        <div className="mt-4 rounded-3xl bg-white p-5 text-[#0B2D4D]">
          <div className="flex gap-4">
            <img
              src={detail.image || "/dog-placeholder.svg"}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
              alt={detail.dog_name}
              className="h-24 w-24 rounded-2xl object-cover"
            />
            <div>
              <p className="font-display text-xl font-black">🆘 {detail.dog_name}</p>
              <p className="text-sm text-slate-500">{detail.dog_breed}</p>
              <p className="mt-1 text-xs text-slate-500">Dueño: {detail.owner_name || "—"}</p>
              <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold">
                {detail.status}
              </span>
            </div>
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <p><b>Última vez visto:</b> {detail.last_seen_location_label}</p>
            {detail.last_seen_at && <p><b>Cuándo:</b> {detail.last_seen_at}</p>}
            <p><b>Radio de aviso:</b> {detail.radius_km} km</p>
            <p><b>Push geolocalizado:</b> {detail.push_metrics ? `${detail.push_metrics.eligible ?? 0} elegibles · ${detail.push_metrics.stale_location_excluded ?? 0} ubicación vencida · ${detail.push_metrics.attempted ?? 0} intentados · ${detail.push_metrics.sent ?? 0} enviados` : "—"}</p>
            {detail.description && <p><b>Info:</b> {detail.description}</p>}
            {detail.distinctive_features && <p><b>Características:</b> {detail.distinctive_features}</p>}
            {detail.collar_or_harness && <p><b>Collar/arnés:</b> {detail.collar_or_harness}</p>}
            {detail.behavior_notes && <p><b>Comportamiento:</b> {detail.behavior_notes}</p>}
            <p className="text-xs text-slate-400">Creada: {detail.created_at ? new Date(detail.created_at).toLocaleString() : "—"}</p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {detail.status !== "closed" && (
              <Button variant="outline" onClick={() => changeStatus(detail, "closed")} testId="sos-admin-close">
                Cerrar / desactivar
              </Button>
            )}
          </div>

          <div className="mt-5">
            <p className="text-sm font-bold">👀 Avistamientos ({sightings ? sightings.length : "…"})</p>
            {sightings && sightings.length === 0 && (
              <p className="mt-2 text-xs text-slate-400" data-testid="admin-sos-sightings-empty">Sin avistamientos.</p>
            )}
            <div className="mt-2 space-y-2">
              {(sightings || []).map((s) => (
                <div key={s.id} className="rounded-2xl bg-slate-50 p-3 text-sm" data-testid={`admin-sighting-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold">📍 {s.location_label}</span>
                    <button
                      onClick={() => toggleSighting(s)}
                      data-testid={`admin-sighting-toggle-${s.id}`}
                      className={`rounded-full px-3 py-1 text-xs font-bold ${s.status === "removed" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}
                    >
                      {s.status === "removed" ? "Restaurar" : "Eliminar"}
                    </button>
                  </div>
                  {s.note && <p className="mt-1 text-slate-500">{s.note}</p>}
                  {s.lat != null && s.lng != null && (
                    <a
                      href={`https://www.google.com/maps?q=${s.lat},${s.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-sighting-map-${s.id}`}
                      className="mt-1 inline-block text-xs font-bold text-[#0B2D4D] underline"
                    >
                      📍 Ver ubicación en Maps
                    </a>
                  )}
                  {s.status === "removed" && <p className="mt-1 text-xs font-bold text-rose-500">(oculto)</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-sos" className="text-white">
      <h3 className="text-lg font-semibold">PawCita SOS</h3>
      <div className="mt-3 flex flex-wrap gap-2">
        {SOS_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`admin-sos-filter-${f.key}`}
            className={`rounded-full px-4 py-1.5 text-sm font-bold transition ${
              filter === f.key ? "bg-white text-[#0B2D4D]" : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {err && <p className="mt-2 text-sm text-red-300">{err}</p>}
      {list === null ? (
        <p className="mt-4 text-sm text-white/50">Cargando…</p>
      ) : list.length === 0 ? (
        <p className="mt-6 text-sm text-white/60" data-testid="admin-sos-empty">
          No hay alertas en este estado.
        </p>
      ) : (
        <div className="mt-4 space-y-2">
          {list.map((a) => (
            <div
              key={a.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/10 p-4"
              data-testid={`admin-sos-row-${a.id}`}
            >
              <div className="min-w-0">
                <p className="font-semibold">🆘 {a.dog_name} · {a.status}</p>
                <p className="text-xs text-white/60">
                  {a.owner_name || "—"} · {a.last_seen_location_label} · 👀 {a.sighting_count ?? 0} · {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => openDetail(a)} testId={`sos-admin-open-${a.id}`}>
                  Ver
                </Button>
                {a.status !== "closed" && (
                  <Button variant="outline" onClick={() => changeStatus(a, "closed")} testId={`sos-admin-close-${a.id}`}>
                    Cerrar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
